import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * GoalCanvas — the goals whiteboard (`tgo` family).
 * docs/GOAL-CANVAS-VISION.md — the pipeline this surface serves:
 *
 *   SPARK → BRAINSTORM → WALK-BACK → SMART GOAL → PLAN → TASKS
 *
 * RUNG 2 (review §3): a real canvas, not a div. Objects live in WORLD
 * COORDINATES (unbounded px, persisted as canvas_x/canvas_y); the viewport
 * pans (drag the background, scroll) and zooms (pinch / ctrl+wheel / HUD)
 * around them, so a layout the user builds STAYS where they put it — the
 * space no longer breathes with the goal count.
 *
 * Two object kinds live on the board:
 *
 *   THOUGHTS  sticky notes — cheap, draggable, hard-deleted. Enter throws
 *             one (brainstorm-first by design) near the viewport center.
 *             Drop a sticky ON a goal card to attach it: it STAYS VISIBLE,
 *             parked where it was dropped, tethered to its goal by a drawn
 *             line ("visually tethered", vision §2 — the chip is gone).
 *             Each attached sticky detaches individually.
 *
 *   GOALS     cards carrying the five S·M·A·R·T facets as letters that
 *             light as they're answered (A is EARNED by a plan, and
 *             clicking it runs on-demand decomposition NOW — fed by the
 *             attached brainstorm and people).
 *
 * AIM SMALL, MISS SMALL: >4 active goals draws the over-commitment banner;
 * P1 is THE ONE and wears the accent ring.
 *
 * Presentational + MUI-free. All persistence rides host callbacks.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CalendarPlus, Check, ChevronDown, ChevronsDown, ChevronsUp, Copy, Expand, Flag, Frame, HelpCircle, Image as ImageIcon, Lasso, Link2, ListChecks, Loader2, Maximize2, Minus, MousePointer2, Palette, PanelRight, Pause, Plus, Circle, Diamond, MoveRight, PenLine, Redo2, ScanText, Shrink, Sparkles, Square, Droplet, Target, Trash2, Undo2, Unlink, UserPlus, Wand2, X } from 'lucide-react';
import { PhotoImport } from './PhotoImport.js';
import { usePanZoom, useUndoStack, useSelection, boundsOf, clipToBox, centerOf, rectFromDrag, marqueeHits, lassoHits, simplifyPath, pathFromPoints, } from '../../canvas/index.js';
const LETTER_QUESTION = {
    S: 'Specific? Name where this lands — attach the account.',
    M: 'Measurable? How will you know it happened — a number beats a feeling.',
    A: 'Achievable? Break it into steps — run the decomposition on your brainstorm.',
    R: 'Relevant? Why does this matter — what does it defend or unlock?',
    T: 'Time-bound? Give it a horizon it can miss.',
};
/** Aim small, miss small: past this many active goals, focus is a fiction. */
const AIM_SMALL_MAX = 4;
/** Board paint. MIRROR of apps/backend goalsCore.GOAL_COLORS (the server is
 *  the authority — `chat-tools.test.js` pins the two lists together); the
 *  CSS `tgo-tone-*` family owns the actual colour per theme. */
export const CANVAS_COLORS = ['sun', 'coral', 'rose', 'grape', 'sky', 'mint', 'slate'];
const toneClass = (c) => (c && CANVAS_COLORS.includes(c) ? `tgo-toned tgo-tone-${c}` : '');
const SHAPES = [
    { kind: 'rect', label: 'Box' },
    { kind: 'ellipse', label: 'Circle' },
    { kind: 'diamond', label: 'Decision' },
    { kind: 'arrow', label: 'Arrow' },
];
/**
 * A drawn mark sits either BEHIND the thinking or in front of it, and the
 * sign of `z` is which. That is the whole z-order model, and it is
 * deliberately not a full stacking context across every object type.
 *
 * The review's exit criterion is "sketch a quadrant, drop stickies into
 * it, circle two of them in ink" — which needs exactly these two
 * positions: the quadrant behind the notes, the circle over them. A
 * general z across goals, notes, frames and marks would mean one sorted
 * render list for the whole board, and would let a sticky end up behind
 * a wash where nothing could reach it.
 */
const BEHIND = -1;
const INFRONT = 1;
const CROP_H = 132;
function cropStyle(box) {
    if (!box || box.length !== 4)
        return {};
    const [x, y, w, h] = box;
    if (!(w > 0) || !(h > 0))
        return {};
    const k = CROP_H / h;
    return {
        position: 'absolute',
        // Nothing here is a percentage of the image's own size, which the
        // browser has not necessarily measured yet: scaling by the box's
        // height in its own pixels is exact from the first paint.
        height: 'auto',
        width: 'auto',
        maxWidth: 'none',
        transform: `scale(${k})`,
        transformOrigin: 'top left',
        left: -x * k,
        top: -y * k,
    };
}
/** How many board actions ⌘Z can walk back. */
const UNDO_MAX = 40;
const AIM_DISMISS_KEY = 'tgo-aim-dismissed';
const CARD_W = 264; // px — drag math and tether anchors need real widths
const CARD_H = 190; // estimated card height for fit/tether math
const NOTE_W = 200;
const NOTE_H = 84;
/** World-coordinate hard bounds — "infinite" with a sanity fence. */
const WORLD_MAX = 20000;
const Z_MIN = 0.25;
const Z_MAX = 2.5;
const VIEW_KEY = 'tgo-view-v1';
const clampWorld = (n) => Math.max(-WORLD_MAX, Math.min(WORLD_MAX, n));
function smartState(g, planExists) {
    return {
        S: !!g.account_id || (g.outcome || '').trim().length >= 60,
        M: !!g.measure,
        A: planExists,
        R: !!g.why,
        T: !!g.horizon,
    };
}
export function GoalCanvas({ goals, notes, tasksByGoal, accounts, people, onCreate, onUpdateGoal, onUpdateStatus, onDecomposeGoal, onCreateNote, onUpdateNote, onDeleteNote, onBookRhythm, pipeline, onOpenNumber, onSuggestWalkBack, onUpdateDeal, p1Share, onOpenRegistries, onMoveMany, objects, edges, onCreateObject, onUpdateObject, onDeleteObject, onCreateEdge, onUpdateEdge, onDeleteEdge, onPolishRegion, onProcessRegion, onCommitRegion, onImportPhoto, onReadPhoto, onCommitStickies, }) {
    const cardRefs = useRef(new Map());
    /**
     * MEASURED sizes, keyed by object id. `CARD_H`/`NOTE_H` were estimates
     * ("estimated card height for fit/tether math") while stickies grow with
     * their text and cards with their content — so arrows aimed at a box that
     * was not there, fit-everything mis-framed, and the marquee hit-tested a
     * fiction. A ResizeObserver reports the real box; the constants survive
     * only as first-paint fallback and as spacing values.
     *
     * offsetWidth/Height, never getBoundingClientRect: objects live inside the
     * scaled world, and the rect would come back multiplied by the zoom.
     */
    const measured = useRef({});
    const [sizes, setSizes] = useState({});
    const roRef = useRef(null);
    const flushRef = useRef(0);
    const refCbs = useRef(new Map());
    /**
     * Created on FIRST REF ATTACH, not in an effect. Ref callbacks run during
     * commit and effects run after, so an effect-created observer does not
     * exist yet when the board's objects attach — every one of them called
     * `ro?.observe` on null and nothing was ever measured. Silent, and only
     * visible by driving the board.
     */
    const ensureRO = useCallback(() => {
        if (roRef.current || typeof ResizeObserver === 'undefined')
            return roRef.current;
        const ro = new ResizeObserver((entries) => {
            let changed = false;
            for (const entry of entries) {
                const el = entry.target;
                const id = el.dataset.objId;
                if (!id)
                    continue;
                const w = el.offsetWidth;
                const h = el.offsetHeight;
                if (!w && !h)
                    continue; // unmounting
                const prev = measured.current[id];
                if (!prev || Math.abs(prev.w - w) > 0.5 || Math.abs(prev.h - h) > 0.5) {
                    measured.current[id] = { w, h };
                    changed = true;
                }
            }
            // Coalesce to one state write per frame — a board of 60 objects
            // reports 60 entries on the first paint and on every zoom-free reflow.
            if (changed && !flushRef.current) {
                flushRef.current = requestAnimationFrame(() => {
                    flushRef.current = 0;
                    setSizes({ ...measured.current });
                });
            }
        });
        roRef.current = ro;
        return ro;
    }, []);
    useEffect(() => () => {
        roRef.current?.disconnect();
        roRef.current = null;
        if (flushRef.current)
            cancelAnimationFrame(flushRef.current);
        flushRef.current = 0;
    }, []);
    /** A ref callback that is STABLE per id — an inline arrow changes identity
     *  every render, which would detach and re-observe every object each time.
     *  Goals also register in `cardRefs`, which the sticky-drop hit tests walk. */
    const objRef = useCallback((id, isGoal) => {
        let cb = refCbs.current.get(id);
        if (!cb) {
            cb = (el) => {
                const ro = ensureRO();
                const prev = elRefs.current.get(id);
                if (prev && prev !== el && ro)
                    ro.unobserve(prev);
                if (el) {
                    el.dataset.objId = id;
                    elRefs.current.set(id, el);
                    if (isGoal)
                        cardRefs.current.set(id, el);
                    ro?.observe(el);
                }
                else {
                    elRefs.current.delete(id);
                    cardRefs.current.delete(id);
                    refCbs.current.delete(id);
                    delete measured.current[id];
                }
            };
            refCbs.current.set(id, cb);
        }
        return cb;
    }, [ensureRO]);
    const elRefs = useRef(new Map());
    // The viewport is the shared canvas primitive now (packages/ui/src/canvas):
    // it brings two-finger pinch — which this board advertised in its header and
    // never had — plus deltaMode-correct wheel and eased programmatic moves.
    const pz = usePanZoom({
        storageKey: VIEW_KEY,
        minZoom: Z_MIN,
        maxZoom: Z_MAX,
        ignoreWheelWithin: '.tgo-hud, .tgo-dock-wrap',
        // Only the board's own background pans; objects handle their own drags.
        // Figma/Miro convention: plain background drag SELECTS, and pan moves to
        // space-drag, the middle button and two fingers. Touch always pans and
        // pinches — there is no marquee gesture with a finger; that is the lasso.
        shouldPan: (e) => {
            const el = e.target;
            const onBackground = el === e.currentTarget
                || el.classList.contains('tgo-world')
                || !!el.closest('.tgo-blank');
            if (!onBackground)
                return false;
            if (e.pointerType !== 'mouse')
                return true;
            return e.button === 1 || spaceRef.current;
        },
    });
    const { view, setView, viewRef, screenToWorld, panning } = pz;
    const viewportRef = pz.containerRef;
    const [seedText, setSeedText] = useState('');
    const [seedBusy, setSeedBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState('');
    const [localPos, setLocalPos] = useState({});
    /** Synchronous mirror — the debounced nudge flush runs outside a render. */
    const localPosRef = useRef(localPos);
    localPosRef.current = localPos;
    /** In-flight resize, applied inline; the ResizeObserver keeps the board's
     *  geometry in step on its own, so this only drives the element. */
    const [localSize, setLocalSize] = useState({});
    const resizeRef = useRef(null);
    const [abandonArmed, setAbandonArmed] = useState(null);
    const [noteArmed, setNoteArmed] = useState(null);
    const [decomposing, setDecomposing] = useState(null);
    /** Re-decompose is armed (two-click): it replaces the plan's OPEN steps. */
    const [redecomposeArmed, setRedecomposeArmed] = useState(null);
    /** The object being actively dragged (past threshold) — z-elevated. */
    const [dragging, setDragging] = useState(null);
    /** The goal card a dragged sticky is currently hovering — highlighted. */
    const [dropTarget, setDropTarget] = useState(null);
    /** Walk-back mode: an open "what must be true before this?" input on the
     *  board, creating precursor `order` for `goalId` at world (x, y). */
    const [walkFor, setWalkFor] = useState(null);
    const [walkDraft, setWalkDraft] = useState('');
    const [walkBusy, setWalkBusy] = useState(false);
    /** The dock tray: deal chips on the board's edge (vision §4). */
    const [dockOpen, setDockOpen] = useState(false);
    /** An in-flight deal-chip drag: ghost at viewport (x, y). */
    const [dealGhost, setDealGhost] = useState(null);
    const [dockZone, setDockZone] = useState(null);
    const dealDragRef = useRef(null);
    const potRef = useRef(null);
    const benchRef = useRef(null);
    const dragRef = useRef(null);
    /** Pointer capture makes a card's `click` fire after a DRAG too — connect
     *  mode must not link a goal the user was merely moving. */
    const lastGestureMoved = useRef(false);
    /** Full-page mode: the board takes the viewport (Esc or the HUD to leave). */
    const [full, setFull] = useState(false);
    /** Multi-select. Until this existed the board could only ever act on one
     *  object, which is what made it un-brainstormable (canvas review §B). */
    const sel = useSelection();
    const [tool, setTool] = useState('select');
    /** Which shape the shape tool draws. Sticky, because you draw five
     *  boxes in a row far more often than you alternate. */
    const [shapeKind, setShapeKind] = useState('rect');
    /** The ink stroke being drawn right now, in world points. */
    const [inking, setInking] = useState(null);
    /** The shape being dragged out right now. */
    const [shaping, setShaping] = useState(null);
    /** The in-flight region gesture, in WORLD coords. */
    const [marquee, setMarquee] = useState(null);
    const [lasso, setLasso] = useState(null);
    /** One gesture type for every empty-board drag: select by rectangle, select
     *  by loop, draw a stroke, drag out a shape. They share the pointer flow
     *  and differ only in what the points mean when the pointer lifts. */
    const regionRef = useRef(null);
    /** Drawing a connector: the anchor it started from and where the pointer
     *  is now, in world coords. */
    const [edgeDraw, setEdgeDraw] = useState(null);
    const edgeRef = useRef(null);
    /** The connector whose label is being written. */
    const [edgeEditing, setEdgeEditing] = useState(null);
    /** A region verb in flight, and the proposal Process came back with. */
    const [regionBusy, setRegionBusy] = useState(null);
    const [proposal, setProposal] = useState(null);
    /** Which proposed actions the human has ticked. Default: all of them —
     *  the point is to strike the wrong ones, not to re-type the right ones. */
    const [accepted, setAccepted] = useState(new Set());
    const [committing, setCommitting] = useState(false);
    /** The "it landed" toast. `noun` because two different verbs feed it —
     *  Process makes tasks, the photo read makes stickies — and a toast that
     *  says the wrong word is worse than no toast. */
    const [committed, setCommitted] = useState(null);
    const [photoOpen, setPhotoOpen] = useState(false);
    /** A photo read in flight, and what it came back with. Same shape as a
     *  region proposal on purpose: nothing is on the board until it is ticked
     *  and pressed, and the photograph stays underneath either way. */
    const [readingId, setReadingId] = useState(null);
    const [shot, setShot] = useState(null);
    const [takenIn, setTakenIn] = useState(new Set());
    const [takenRegions, setTakenRegions] = useState(new Set());
    const [takenLinks, setTakenLinks] = useState(new Set());
    const [takingIn, setTakingIn] = useState(false);
    /** The review walk (P3): which unchecked sticky we are standing on.
     *  Named "check", not "walk", because walk-BACK above is a different
     *  feature entirely and one word cannot mean both. */
    const [checkAt, setCheckAt] = useState(null);
    const [checkDraft, setCheckDraft] = useState('');
    /** Enter commits directly rather than proxying through blur: if focus
     *  never landed (or the user clicks straight onto another chip) a
     *  blur-only editor silently loses what was typed. This marks what Enter
     *  already saved so the trailing blur does not save it twice. */
    const justCommitted = useRef(null);
    /** Space turns drag into pan, the way every canvas tool does it. */
    const spaceRef = useRef(false);
    const [spacePan, setSpacePan] = useState(false);
    /** Double-click on empty board opens a composer THERE — create where you
     *  are looking, instead of a round trip to the bar at the top. */
    const [composeAt, setComposeAt] = useState(null);
    const [composeText, setComposeText] = useState('');
    /** Spawn slots for the throw bar. The old cascade was `(n % 4) * 36`, so
     *  every 4th thought landed exactly on an earlier one (canvas review D1). */
    const spawnSeq = useRef(0);
    /** Undo + redo (⌘Z / ⌘⇧Z or the HUD) — the shared stack. */
    const history = useUndoStack({ max: UNDO_MAX });
    const { push: pushUndo, retire: retireUndo, run: withUndo, undo, redo } = history;
    /** The open swatch row (one at a time), keyed by goal/note id. */
    const [paletteFor, setPaletteFor] = useState(null);
    /** Connect mode: the goal (or loose sticky) whose link is being drawn;
     *  the next goal clicked becomes its target. */
    const [linkFrom, setLinkFrom] = useState(null);
    /** The over-commitment banner is a verdict, not a control — dismissable,
     *  and it comes back only when the count changes. */
    const [aimDismissed, setAimDismissed] = useState(() => {
        try {
            const v = window.localStorage.getItem(AIM_DISMISS_KEY);
            return v ? Number(v) : null;
        }
        catch {
            return null;
        }
    });
    // Connect mode ends when its source leaves the board (paused from the
    // drawer, deleted, attached by drop) — a dangling prompt would link the
    // next click to nothing. Entering it closes any open editor: target cards
    // lose their pointer events, so an open textarea would commit by blur on
    // the same click that links.
    useEffect(() => {
        if (!linkFrom)
            return;
        setEditing(null);
        const alive = linkFrom.kind === 'goal'
            ? goals.some((g) => g.id === linkFrom.id)
            : (notes || []).some((n) => n.id === linkFrom.id && !n.goal_id);
        if (!alive)
            setLinkFrom(null);
    }, [linkFrom, goals, notes]);
    // Full page: the page behind must not scroll under the board, and the
    // HOST decides what of its own chrome steps aside — this package knows
    // nothing about chat bubbles or rails, it only raises the flag
    // (`html.tgo-full-page`; TekStak hides its chat FAB on it).
    useEffect(() => {
        if (!full)
            return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('tgo-full-page');
        return () => {
            document.body.style.overflow = prev;
            document.documentElement.classList.remove('tgo-full-page');
        };
    }, [full]);
    /** World position of an object: local drag wins, then persisted, then a
     *  deterministic default near the origin (legacy rows without one). */
    const posOf = useCallback((id, x, y, i = 0, note = false) => {
        const local = localPos[id];
        if (local)
            return local;
        if (x != null && y != null)
            return { x, y };
        return note
            ? { x: 32 + (i % 5) * (NOTE_W + 28), y: 24 + Math.floor(i / 5) * (NOTE_H + 36) }
            : { x: 48 + (i % 3) * (CARD_W + 48), y: 250 + Math.floor(i / 3) * (CARD_H + 70) };
    }, [localPos]);
    /** Grab a region by its title bar. Its body never takes the pointer, so a
     *  rubber-band drawn across a region still selects the thinking inside. */
    const startFrameDrag = (e, f) => {
        const el = e.target;
        if (el.closest('input, textarea, button'))
            return;
        e.stopPropagation();
        dragRef.current = {
            kind: 'frame', id: f.id, host: e.currentTarget, pointerId: e.pointerId,
            startX: e.clientX, startY: e.clientY,
            objStart: { x: f.x, y: f.y },
            lastX: e.clientX, lastY: e.clientY, moved: false,
            origPos: localPos[f.id] || null,
            cur: null,
            groupCur: null,
            // Whatever is standing inside travels with it — but ONLY for a
            // region, which is the thing that means "these belong together".
            // A shape or a stroke has a bounding box too, and carrying every
            // sticky inside a squiggle's box would move work the user never
            // pointed at. Drawing over your thinking must not capture it.
            group: f.kind === 'frame'
                ? containedBy(f).map((o) => ({ id: o.id, kind: o.kind, from: { x: o.x, y: o.y } }))
                : [],
            tappedControl: false,
        };
    };
    /** Persist a set of moved objects — one request when the host wired the
     *  batch endpoint, otherwise the per-object callbacks. */
    const persistMoves = useCallback((moves) => {
        if (!moves.length)
            return Promise.resolve();
        if (onMoveMany)
            return Promise.resolve(onMoveMany(moves));
        return Promise.all(moves.map((m) => (m.kind === 'goal'
            ? (onUpdateGoal ? onUpdateGoal(m.id, { canvas_x: m.canvas_x, canvas_y: m.canvas_y }) : undefined)
            : (onUpdateNote ? onUpdateNote(m.id, { canvas_x: m.canvas_x, canvas_y: m.canvas_y }) : undefined))));
    }, [onMoveMany, onUpdateGoal, onUpdateNote]);
    /** Drop local drag overrides so the persisted positions paint again. */
    const forgetLocal = useCallback((ids) => {
        setLocalPos((lp) => {
            const c = { ...lp };
            for (const id of ids)
                delete c[id];
            return c;
        });
    }, []);
    const goalIds = useMemo(() => new Set(goals.map((g) => g.id)), [goals]);
    /** Attached notes render ON the board, tethered — position defaults to a
     *  slot beside their goal (walk-back precursors: a chain marching LEFT,
     *  the vision's rightward pipeline made literal) for rows without one. */
    const attachedPos = useCallback((n, idxAmongSiblings) => {
        if (localPos[n.id])
            return localPos[n.id];
        if (n.canvas_x != null && n.canvas_y != null)
            return { x: n.canvas_x, y: n.canvas_y };
        const gi = goals.findIndex((g) => g.id === n.goal_id);
        const gp = gi >= 0 ? posOf(goals[gi].id, goals[gi].canvas_x, goals[gi].canvas_y, gi) : { x: 0, y: 0 };
        if (n.walk_order != null) {
            return { x: gp.x - (NOTE_W + 70) * n.walk_order, y: gp.y + 26 };
        }
        return { x: gp.x + CARD_W + 36, y: gp.y + idxAmongSiblings * (NOTE_H + 20) };
    }, [localPos, goals, posOf]);
    const unattached = (notes || []).filter((n) => !n.goal_id);
    const attached = (notes || []).filter((n) => n.goal_id && goalIds.has(n.goal_id));
    // Position every attached sticky ONCE per render — tethers and notes
    // must agree on where each one sits.
    const perGoal = new Map();
    const attachedPlaced = attached.map((n) => {
        const k = perGoal.get(n.goal_id) || 0;
        perGoal.set(n.goal_id, k + 1);
        return { n, p: attachedPos(n, k) };
    });
    // Walk-back chains: per goal, precursors sorted 1..N (1 = nearest the
    // outcome). The TAIL (deepest) is where the next "before this?" asks from.
    const chains = new Map();
    for (const item of attachedPlaced) {
        if (item.n.walk_order == null)
            continue;
        if (!chains.has(item.n.goal_id))
            chains.set(item.n.goal_id, []);
        chains.get(item.n.goal_id).push(item);
    }
    for (const chain of chains.values())
        chain.sort((a, b) => (a.n.walk_order - b.n.walk_order));
    /** Every object as one flat list of boxes: the renderer keeps its two
     *  types, but selection, hit-testing and group moves are written ONCE. */
    const boardObjects = useMemo(() => {
        const out = [];
        const box = (id, p, kind, ow, oh) => {
            const m = sizes[id];
            return {
                id,
                kind,
                x: p.x,
                y: p.y,
                w: m?.w ?? ow ?? (kind === 'goal' ? CARD_W : NOTE_W),
                h: m?.h ?? oh ?? (kind === 'goal' ? CARD_H : NOTE_H),
            };
        };
        goals.forEach((g, i) => out.push(box(g.id, posOf(g.id, g.canvas_x, g.canvas_y, i), 'goal', g.canvas_w, g.canvas_h)));
        // Loose and attached stickies are placed by DIFFERENT rules, and the
        // hit-test has to agree with the renderer — so read the same placement
        // the renderer uses rather than re-deriving it.
        unattached.forEach((n, i) => out.push(box(n.id, posOf(n.id, n.canvas_x, n.canvas_y, i, true), 'note', n.canvas_w, n.canvas_h)));
        attachedPlaced.forEach(({ n, p }) => out.push(box(n.id, p, 'note', n.canvas_w, n.canvas_h)));
        return out;
    }, [goals, unattached, attachedPlaced, posOf, sizes]);
    const boardById = useMemo(() => new Map(boardObjects.map((o) => [o.id, o])), [boardObjects]);
    /** Frames live in the same world but are NOT selectable objects: a
     *  rubber-band inside a region must catch the thinking, not the box drawn
     *  around it. They are grabbed by their title bar instead. */
    /** Photo backdrops: the bottom layer, and never interactive in the body —
     *  the whole point is to draw on top of them. */
    const backdrops = useMemo(() => (objects || [])
        .filter((o) => o.kind === 'image')
        .map((o) => ({ ...o, ...(localPos[o.id] || { x: o.x, y: o.y }), ...(localSize[o.id] ? { w: localSize[o.id].w, h: localSize[o.id].h ?? o.h } : {}) })), [objects, localPos, localSize]);
    /**
     * Ink and shapes (S4), split by whether they sit behind the thinking or
     * over it. Sorted within each half so "bring forward" has somewhere to
     * go — see the note on BEHIND/INFRONT for why this is two layers and
     * not one global stacking order.
     */
    const marks = useMemo(() => {
        const all = (objects || [])
            .filter((o) => o.kind === 'ink' || o.kind === 'shape')
            .map((o) => ({
            ...o,
            ...(localPos[o.id] || { x: o.x, y: o.y }),
            ...(localSize[o.id] ? { w: localSize[o.id].w, h: localSize[o.id].h ?? o.h } : {}),
        }))
            .sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
        return {
            behind: all.filter((o) => (o.z ?? 0) < 0),
            front: all.filter((o) => (o.z ?? 0) >= 0),
        };
    }, [objects, localPos, localSize]);
    const markById = useMemo(() => new Map([...marks.behind, ...marks.front].map((o) => [o.id, o])), [marks]);
    const frames = useMemo(() => (objects || [])
        .filter((o) => o.kind === 'frame')
        .map((o) => ({ ...o, ...(localPos[o.id] || { x: o.x, y: o.y }), ...(localSize[o.id] ? { w: localSize[o.id].w, h: localSize[o.id].h ?? o.h } : {}) })), [objects, localPos, localSize]);
    const frameById = useMemo(() => new Map(frames.map((f) => [f.id, f])), [frames]);
    /** Frames and marks share the drag/resize machinery — both are objects
     *  positioned in world space that are NOT part of the thinking. */
    const dragBoxById = useMemo(() => new Map([...frameById, ...markById]), [frameById, markById]);
    /** Any endpoint an edge can name, in one lookup. */
    const anyBox = useCallback((kind, id) => (kind === 'object' ? frameById.get(id) : boardById.get(id)), [frameById, boardById]);
    /** What is sitting inside a frame right now. Membership is spatial and
     *  never stored — the picture is the truth on a whiteboard, and a stored
     *  parent would drift the moment anything moved. */
    const containedBy = useCallback((f) => (boardObjects.filter((o) => {
        const cx = o.x + o.w / 2;
        const cy = o.y + o.h / 2;
        return cx >= f.x && cx <= f.x + f.w && cy >= f.y && cy <= f.y + f.h;
    })), [boardObjects]);
    // Objects vanish underneath a selection — paused from the drawer, deleted,
    // promoted. A selection holding dead ids applies bulk actions to nothing.
    const liveIds = useMemo(() => new Set(boardObjects.map((o) => o.id)), [boardObjects]);
    const { pruneTo } = sel;
    useEffect(() => { pruneTo(liveIds); }, [liveIds, pruneTo]);
    /** id → goal (+ index) for the segment builder, titles and link commits —
     *  the board re-renders on every drag frame, so no per-card scans. */
    const goalById = useMemo(() => {
        const m = new Map();
        goals.forEach((g, i) => m.set(g.id, { g, i }));
        return m;
    }, [goals]);
    /** Status change with its inverse recorded (pause → resume, etc.). */
    const STATUS_VERB = { paused: 'pause', achieved: 'achieve', abandoned: 'abandon', active: 'resume' };
    const changeStatus = (g, status) => {
        if (!onUpdateStatus)
            return;
        const prev = (g.status || 'active');
        withUndo({ id: g.id, label: `${STATUS_VERB[status]} “${g.outcome.slice(0, 40)}”`, undo: () => onUpdateStatus(g.id, prev) }, () => onUpdateStatus(g.id, status));
    };
    /** Goal edit with the previous values of the touched keys recorded. */
    const editGoal = (g, updates, label) => {
        if (!onUpdateGoal)
            return;
        const prev = {};
        for (const k of Object.keys(updates)) {
            // The S facet is typed as `account` (a name the route resolves); the
            // inverse sends the previous account_id itself — round-tripping the
            // LABEL through the resolver clears the account when it misses.
            if (k === 'account')
                prev.account_id = g.account_id ?? null;
            else
                prev[k] = g[k] ?? null;
        }
        withUndo({ id: g.id, label, undo: () => onUpdateGoal(g.id, prev) }, () => onUpdateGoal(g.id, updates));
    };
    /** Sticky edit with its inverse recorded. */
    const editNote = (n, updates, label) => {
        if (!onUpdateNote)
            return;
        const prev = {};
        for (const k of Object.keys(updates))
            prev[k] = n[k] ?? null;
        withUndo({ id: n.id, label, undo: () => onUpdateNote(n.id, prev) }, () => onUpdateNote(n.id, updates));
    };
    /** Delete is hard on the server — undo re-creates the sticky as it was.
     *  Entries aimed at the old id would 404 (the re-creation has a NEW id),
     *  so they retire once the delete is confirmed. */
    const removeNote = (n, p) => {
        if (!onDeleteNote)
            return;
        if (linkFrom?.id === n.id)
            setLinkFrom(null);
        const recreate = onCreateNote ? () => onCreateNote({
            text: n.text, canvas_x: p.x, canvas_y: p.y,
            ...(n.goal_id ? { goal_id: n.goal_id } : {}),
            ...(n.walk_order != null ? { walk_order: n.walk_order } : {}),
            ...(n.color ? { color: n.color } : {}),
        }) : null;
        void Promise.resolve(onDeleteNote(n.id))
            .then(() => {
            retireUndo(n.id);
            if (recreate)
                pushUndo({ label: 'delete thought', undo: recreate });
        })
            .catch(() => { });
    };
    /** The selected objects, split by kind — most bulk verbs only apply to
     *  thoughts (a goal is a record; you pause or abandon it, never Delete it). */
    const selectedObjects = useMemo(() => [...sel.selected].map((id) => boardById.get(id)).filter((o) => !!o), [sel.selected, boardById]);
    const selectedNotes = useMemo(() => selectedObjects.filter((o) => o.kind === 'note')
        .map((o) => (notes || []).find((n) => n.id === o.id))
        .filter((n) => !!n), [selectedObjects, notes]);
    /** Delete every selected thought at once. Immediate, because brainstorm
     *  objects are cheap and private (vision §8.1) and undo now exists — the
     *  armed "sure?" confirm was friction from before there was a way back. */
    const deleteSelection = useCallback(() => {
        if (!onDeleteNote || !selectedNotes.length)
            return;
        const snapshot = selectedNotes.map((n) => ({
            n, p: boardById.get(n.id) || { x: n.canvas_x || 0, y: n.canvas_y || 0 },
        }));
        const ids = snapshot.map((x) => x.n.id);
        void Promise.all(ids.map((id) => Promise.resolve(onDeleteNote(id)).catch(() => null)))
            .then(() => {
            for (const id of ids)
                retireUndo(id);
            sel.clear();
            if (!onCreateNote)
                return;
            pushUndo({
                label: `delete ${ids.length} thought${ids.length === 1 ? '' : 's'}`,
                ids,
                undo: () => Promise.all(snapshot.map(({ n, p }) => onCreateNote({
                    text: n.text, canvas_x: p.x, canvas_y: p.y,
                    ...(n.goal_id ? { goal_id: n.goal_id } : {}),
                    ...(n.walk_order != null ? { walk_order: n.walk_order } : {}),
                    ...(n.color ? { color: n.color } : {}),
                }))),
            });
        });
    }, [onDeleteNote, onCreateNote, selectedNotes, boardById, retireUndo, pushUndo, sel]);
    /** Copy the selected thoughts, offset so the copies are visibly separate. */
    const duplicateSelection = useCallback(() => {
        if (!onCreateNote || !selectedNotes.length)
            return;
        const made = selectedNotes.map((n) => {
            const p = boardById.get(n.id);
            return onCreateNote({
                text: n.text,
                canvas_x: clampWorld((p?.x ?? 0) + 28),
                canvas_y: clampWorld((p?.y ?? 0) + 28),
                ...(n.color ? { color: n.color } : {}),
            });
        });
        void Promise.all(made.map((m) => Promise.resolve(m).catch(() => null)));
    }, [onCreateNote, selectedNotes, boardById]);
    /** Recolour everything selected in one gesture. */
    const colorSelection = useCallback((c) => {
        for (const o of selectedObjects) {
            if (o.kind === 'goal' && onUpdateGoal) {
                const g = goalById.get(o.id)?.g;
                if (g)
                    editGoal(g, { color: c }, 'recolour goal');
            }
            else if (o.kind === 'note' && onUpdateNote) {
                const n = (notes || []).find((x) => x.id === o.id);
                if (n)
                    editNote(n, { color: c }, 'recolour thought');
            }
        }
        setPaletteFor(null);
    }, [selectedObjects, onUpdateGoal, onUpdateNote, goalById, notes, editGoal, editNote]);
    /** A region's opening name, derived rather than invented: the goal it was
     *  drawn around, else the first thought's opening words. Always editable —
     *  this is a starting point, not a claim. */
    const autoTitle = useCallback(() => {
        const g = selectedObjects.find((o) => o.kind === 'goal');
        if (g)
            return (goalById.get(g.id)?.g.outcome || 'Region').slice(0, 60);
        const n = selectedNotes[0];
        if (n)
            return (n.text.split(/[.\n]/)[0] || '').split(/\s+/).slice(0, 6).join(' ').slice(0, 60) || 'Region';
        return 'Region';
    }, [selectedObjects, selectedNotes, goalById]);
    /** What is standing inside a region, with the geometry the server cannot
     *  measure for itself. */
    const regionPayload = useCallback((f) => ({
        label: f.label || null,
        members: containedBy(f).map((o) => ({ kind: o.kind, id: o.id, x: o.x, y: o.y, w: o.w, h: o.h })),
    }), [containedBy]);
    /**
     * POLISH: tidy the wording in place. One undo entry for the whole pass,
     * because it is one thought ("clean this up"), not N edits.
     */
    const polish = useCallback((f) => {
        if (!onPolishRegion || !onUpdateNote || regionBusy)
            return;
        setRegionBusy({ id: f.id, verb: 'polish' });
        void Promise.resolve(onPolishRegion(f.id, regionPayload(f)))
            .then((res) => {
            if (!res?.rewrites?.length && !res?.title)
                return;
            const before = res.rewrites
                .map((r) => ({ id: r.id, text: (notes || []).find((n) => n.id === r.id)?.text }))
                .filter((b) => b.text != null);
            const titleBefore = f.label ?? null;
            const wantTitle = res.title && res.title !== f.label && onUpdateObject;
            return Promise.all([
                ...res.rewrites.map((r) => onUpdateNote(r.id, { text: r.text })),
                ...(wantTitle ? [onUpdateObject(f.id, { label: res.title })] : []),
            ]).then(() => {
                // Name what actually changed — "polish 0 thoughts" is a lie when
                // the pass only renamed the region.
                const parts = [];
                if (before.length)
                    parts.push(`${before.length} thought${before.length === 1 ? '' : 's'}`);
                if (wantTitle)
                    parts.push('the name');
                pushUndo({
                    label: `polish ${parts.join(' and ')}`,
                    ids: before.map((b) => b.id),
                    undo: () => Promise.all([
                        ...before.map((b) => onUpdateNote(b.id, { text: b.text })),
                        ...(wantTitle ? [onUpdateObject(f.id, { label: titleBefore })] : []),
                    ]),
                });
            });
        })
            .catch(() => { })
            .finally(() => setRegionBusy(null));
    }, [onPolishRegion, onUpdateNote, onUpdateObject, regionBusy, regionPayload, notes, pushUndo]);
    /** PROCESS: read the region and propose. Nothing is written here. */
    const process = useCallback((f) => {
        if (!onProcessRegion || regionBusy)
            return;
        setRegionBusy({ id: f.id, verb: 'process' });
        setCommitted(null);
        setShot(null);
        void Promise.resolve(onProcessRegion(f.id, regionPayload(f)))
            .then((data) => {
            if (!data)
                return;
            setProposal({ regionId: f.id, label: f.label || 'Region', data });
            setAccepted(new Set((data.actions || []).map((_, i) => i)));
        })
            .catch(() => { })
            .finally(() => setRegionBusy(null));
    }, [onProcessRegion, regionBusy, regionPayload]);
    /**
     * Choose who an action is waiting on.
     *
     * The value is either a contact id (a real record) or a bare name (the
     * model's guess, kept because a name you recognise beats an empty box).
     * Both are sent; the server links only the id, so a name it cannot
     * resolve prints without ever attaching work to the wrong person.
     */
    /**
     * What the owner picker should be showing.
     *
     * The model proposes a NAME. When that name is someone in the contacts
     * list, the picker must already be standing on their record — otherwise
     * a correct guess renders as "no owner", the human has to re-pick a
     * person the system already identified, and most of them will not
     * bother. The select matches on option VALUE, and the option values are
     * contact ids, so the name has to be resolved before it can be shown.
     */
    const ownerValue = useCallback((a) => {
        if (a.owner_contact_id)
            return a.owner_contact_id;
        const hint = (a.owner_hint || '').trim();
        if (!hint)
            return '';
        const hit = (people || []).find((pp) => pp.name.toLowerCase() === hint.toLowerCase());
        return hit?.contact_id || hint;
    }, [people]);
    const setOwner = useCallback((i, value) => {
        setProposal((prev) => {
            if (!prev)
                return prev;
            const person = (people || []).find((pp) => pp.contact_id === value);
            const actions = prev.data.actions.map((a, k) => (k === i ? {
                ...a,
                owner_contact_id: person?.contact_id || null,
                owner_email: person?.email || null,
                owner_hint: person?.name || (value || null),
            } : a));
            return { ...prev, data: { ...prev.data, actions } };
        });
    }, [people]);
    /** Turn the ticked lines into real tasks. */
    const commit = useCallback(() => {
        if (!proposal || !onCommitRegion || committing)
            return;
        // Send the RESOLVED owner. A name the model guessed that matches a
        // real contact is an assignment the system already made; leaving it as
        // a bare string would mean the picker showed a person and the task
        // recorded a word.
        const actions = proposal.data.actions
            .filter((_, i) => accepted.has(i))
            .map((a) => {
            const v = ownerValue(a);
            const person = (people || []).find((pp) => pp.contact_id === v);
            return person
                ? { ...a, owner_contact_id: person.contact_id, owner_email: person.email || null, owner_hint: person.name }
                : a;
        });
        if (!actions.length)
            return;
        setCommitting(true);
        void Promise.resolve(onCommitRegion(proposal.regionId, { actions }))
            .then((res) => {
            setCommitted({ n: res?.created?.length || 0, skipped: res?.skipped || 0, noun: 'task' });
            setProposal(null);
        })
            .catch(() => { })
            .finally(() => setCommitting(false));
    }, [proposal, onCommitRegion, committing, accepted, ownerValue, people]);
    /** READ a photo backdrop. Proposes stickies; writes nothing. */
    const readShot = useCallback((objectId) => {
        if (!onReadPhoto || readingId)
            return;
        setReadingId(objectId);
        setShot(null);
        setProposal(null); // the two proposal panels share a corner
        setCommitted(null);
        void Promise.resolve(onReadPhoto(objectId))
            .then((data) => {
            if (!data)
                return;
            setShot({ objectId, data });
            // Pre-tick the confident reads only. A line the model itself marked
            // as a guess should be a deliberate choice, not something that slips
            // onto the board because the whole list was checked by default.
            // Pre-tick what a person would tick anyway. On a first import that
            // is every confident read. On a RE-import it is only what actually
            // changed: a wall that has not moved should present as "nothing to
            // do here", not as thirty boxes to untick one at a time.
            setTakenIn(new Set((data.stickies || [])
                .map((st, i) => ((st.status ?? 'new') !== 'same' && st.legibility >= 0.6 ? i : -1))
                .filter((i) => i >= 0)));
            // A drawn box and a drawn line are things you can SEE in the photo
            // behind them, so they start ticked. The uncertainty in structure
            // is which way an arrow points, and that is already handled by
            // demoting an unsure arrow to a plain line before it gets here.
            setTakenRegions(new Set((data.regions || [])
                .map((r, i) => ((r.status ?? 'new') === 'same' ? -1 : i))
                .filter((i) => i >= 0)));
            setTakenLinks(new Set((data.connectors || []).map((_, i) => i)));
        })
            .catch(() => { })
            .finally(() => setReadingId(null));
    }, [onReadPhoto, readingId]);
    /**
     * A connector needs both of its ends to EXIST after this commit.
     *
     * An end qualifies two ways: the sticky is ticked and about to be
     * created, or it is already on the board (a re-import match), in which
     * case there is a real note to join even though nothing about it is
     * being written. Without the second case, drawing a new arrow between
     * two old stickies and photographing the board again would find the
     * arrow and then silently drop it, which is the most obvious thing
     * anyone would try on a second pass.
     */
    const endExists = useCallback((i) => (takenIn.has(i) || !!shot?.data.stickies[i]?.match_id), [takenIn, shot]);
    const linkIsLive = useCallback((c) => (endExists(c.from_index) && endExists(c.to_index)), [endExists]);
    /** Put the ticked stickies, regions and connectors on the board, on top
     *  of the photo. The sticky's INDEX travels with it so the server can
     *  resolve a connector's ends to the rows it just created. */
    /** How many connectors would actually be created if pressed now. */
    const liveLinks = useMemo(() => (shot?.data.connectors || [])
        .filter((c, i) => takenLinks.has(i) && linkIsLive(c)).length, [shot, takenLinks, linkIsLive]);
    const takeStickies = useCallback(() => {
        if (!shot || !onCommitStickies || takingIn)
            return;
        const stickies = shot.data.stickies
            .map((st, index) => ({
            ...st,
            index,
            // The row said "move it" or "fix the words"; that is what gets
            // sent, so the server updates the existing note instead of
            // landing a second copy of it beside the first.
            action: st.status === 'moved' ? 'move'
                : st.status === 'edited' ? 'retext'
                    : undefined,
        }))
            .filter((st) => takenIn.has(st.index));
        const regions = (shot.data.regions || []).filter((_, i) => takenRegions.has(i));
        const connectors = (shot.data.connectors || [])
            .filter((c, i) => takenLinks.has(i) && linkIsLive(c));
        // Ends that are already on the board: the server cannot know which
        // existing note a proposal index refers to, and this is the only
        // place that mapping exists.
        const anchors = {};
        shot.data.stickies.forEach((st, i) => {
            if (st.match_id && !takenIn.has(i))
                anchors[i] = st.match_id;
        });
        if (!stickies.length && !regions.length && !connectors.length)
            return;
        setTakingIn(true);
        void Promise.resolve(onCommitStickies(shot.objectId, { stickies, regions, connectors, anchors }))
            .then((res) => {
            setCommitted({ n: res?.count ?? stickies.length, skipped: 0, noun: 'sticky' });
            setShot(null);
        })
            .catch(() => { })
            .finally(() => setTakingIn(false));
    }, [shot, onCommitStickies, takingIn, takenIn, takenRegions, takenLinks, linkIsLive]);
    // ── The review walk (P3) ──────────────────────────────────────────────
    //
    // A count of unchecked stickies is a nag; a walk is a task you can
    // finish. It steps through them in board order, moves the viewport onto
    // each one so the PHOTOGRAPH is underneath it, and offers the three
    // answers that actually exist: the reading is right, the reading is
    // wrong and here is what it says, or this is not a note at all.
    /** Everything read off a photo that nobody has looked at yet, in reading
     *  order — the order they sit on the wall, not the order they arrived. */
    const unsure = useMemo(() => (notes || [])
        .filter((n) => n.needs_review && n.canvas_x != null && n.canvas_y != null)
        .sort((a, b) => (a.canvas_y - b.canvas_y) || (a.canvas_x - b.canvas_x)), [notes]);
    const checkNote = useMemo(() => (checkAt ? unsure.find((n) => n.id === checkAt.id) || null : null), [checkAt, unsure]);
    /** The photograph this sticky was read from, if it is still on the
     *  board. Deleting the backdrop is allowed, and the walk has to keep
     *  working without it — the transcription is still checkable by eye. */
    const checkShot = useMemo(() => {
        const objId = checkNote?.source_ref?.object_id;
        if (!objId)
            return null;
        return (objects || []).find((o) => o.id === objId && o.kind === 'image') || null;
    }, [checkNote, objects]);
    /** Move to a sticky and put it in the middle of the screen, so the
     *  answer is on screen rather than somewhere the user has to go find. */
    const checkTo = useCallback((n) => {
        if (!n) {
            setCheckAt(null);
            return;
        }
        setCheckAt({ id: n.id });
        setCheckDraft(n.text);
        const w = localSize[n.id]?.w ?? n.canvas_w ?? NOTE_W;
        const h = localSize[n.id]?.h ?? n.canvas_h ?? NOTE_H;
        const x = n.canvas_x || 0;
        const y = n.canvas_y || 0;
        // Generous padding rather than a tight fit: the point is to see the
        // sticky IN ITS PLACE on the photograph, with its neighbours around
        // it, because that context is half of what makes a bad reading
        // obvious.
        pz.fitTo({ minX: x, minY: y, maxX: x + w, maxY: y + h }, { pad: 320, maxZoom: 1 });
    }, [pz, localSize]);
    const checkStep = useCallback((delta) => {
        if (!unsure.length) {
            setCheckAt(null);
            return;
        }
        const at = checkAt ? unsure.findIndex((n) => n.id === checkAt.id) : -1;
        // Clearing a sticky shrinks the list, so the NEXT one has slid into
        // the index we just left — stepping forward from a removed item must
        // not skip its replacement.
        const next = at < 0 ? 0 : (at + delta + unsure.length) % unsure.length;
        checkTo(unsure[next]);
    }, [unsure, checkAt, checkTo]);
    /** "That reading is right." Clears the flag; the legibility the model
     *  recorded is left alone, because it is a measurement of the import and
     *  not a thing the human is being asked to overwrite. */
    const checkConfirm = useCallback((n, text) => {
        if (!onUpdateNote)
            return;
        const fixed = (text ?? '').trim();
        const changed = fixed && fixed !== n.text;
        const back = { text: n.text, needs_review: true };
        withUndo({ id: n.id, label: changed ? 'fix a read' : 'check a read', undo: () => onUpdateNote(n.id, back) }, () => onUpdateNote(n.id, changed
            ? { text: fixed, needs_review: false }
            : { needs_review: false }));
        // Step to whatever has taken this one's place in the list.
        const at = unsure.findIndex((x) => x.id === n.id);
        const rest = unsure.filter((x) => x.id !== n.id);
        checkTo(rest.length ? rest[Math.min(at, rest.length - 1)] : null);
    }, [onUpdateNote, withUndo, unsure, checkTo]);
    /** Save a region's name. Called by Enter and by clicking away. */
    const commitFrameTitle = useCallback((f, raw) => {
        setEditing(null);
        const v = raw.trim();
        if (!onUpdateObject || v === (f.label || ''))
            return;
        const back = { label: f.label ?? null };
        withUndo({ id: f.id, label: 'rename region', undo: () => onUpdateObject(f.id, back) }, () => onUpdateObject(f.id, { label: v || null }));
    }, [onUpdateObject, withUndo]);
    /**
     * One drawn mark: an ink stroke or a shape.
     *
     * Both are SVG in a box, so both scale by their viewBox when resized
     * and neither needs its geometry rewritten to move. The bar only
     * appears on hover or while selected, because a board full of always-on
     * chrome is the thing that stops a canvas feeling like paper.
     */
    const renderMark = (o) => {
        const fill = o.content?.fill || 'wash';
        const shape = o.content?.shape || 'rect';
        const d = String(o.content?.d || '');
        const vb = o.content?.vb || { w: o.w, h: o.h };
        const weight = Number(o.content?.weight) || 3;
        return (_jsxs("div", { className: `tgo-mark tgo-mark-${o.kind} ${(o.z ?? 0) < 0 ? 'tgo-mark-behind' : 'tgo-mark-front'} ${toneClass(o.color)} tgo-fill-${fill} ${dragging === o.id ? 'tgo-dragging' : ''}`, style: { left: o.x, top: o.y, width: o.w, height: o.h }, children: [_jsx("svg", { className: "tgo-mark-art", viewBox: `0 0 ${Math.max(1, vb.w)} ${Math.max(1, vb.h)}`, preserveAspectRatio: "none", "aria-hidden": "true", children: o.kind === 'ink' ? (_jsx("path", { d: d, strokeWidth: weight, vectorEffect: "non-scaling-stroke" })) : shape === 'ellipse' ? (_jsx("ellipse", { cx: vb.w / 2, cy: vb.h / 2, rx: Math.max(1, vb.w / 2 - 1), ry: Math.max(1, vb.h / 2 - 1) })) : shape === 'diamond' ? (_jsx("polygon", { points: `${vb.w / 2},1 ${vb.w - 1},${vb.h / 2} ${vb.w / 2},${vb.h - 1} 1,${vb.h / 2}` })) : shape === 'arrow' ? (_jsx("polygon", { points: `1,${vb.h * 0.32} ${vb.w * 0.62},${vb.h * 0.32} ${vb.w * 0.62},2 ${vb.w - 1},${vb.h / 2} ${vb.w * 0.62},${vb.h - 2} ${vb.w * 0.62},${vb.h * 0.68} 1,${vb.h * 0.68}` })) : (_jsx("rect", { x: 1, y: 1, width: Math.max(1, vb.w - 2), height: Math.max(1, vb.h - 2), rx: 6 })) }), _jsxs("div", { className: "tgo-mark-bar", onPointerDown: (e) => startFrameDrag(e, o), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer, children: [onUpdateObject && (_jsxs(_Fragment, { children: [_jsx("span", { className: "tgo-mark-paint", children: CANVAS_COLORS.map((c) => (_jsx("button", { type: "button", className: `tgo-swatch tgo-tone-${c} ${o.color === c ? 'tgo-swatch-on' : ''}`, title: c, onClick: () => paintMark(o, c) }, c))) }), o.kind === 'shape' && (_jsx("button", { type: "button", className: "tgo-mini", title: fill === 'solid' ? 'Solid — click for outline' : fill === 'wash' ? 'Wash — click for solid' : 'Outline — click for wash', onClick: () => cycleFill(o, fill), children: fill === 'solid' ? _jsx(Square, { size: 10 }) : fill === 'wash' ? _jsx(Droplet, { size: 10 }) : _jsx(Frame, { size: 10 }) })), _jsx("button", { type: "button", className: "tgo-mini", title: (o.z ?? 0) < 0 ? 'Behind the thinking — click to bring it in front' : 'In front — click to send it behind', onClick: () => flipDepth(o), children: (o.z ?? 0) < 0 ? _jsx(ChevronsUp, { size: 10 }) : _jsx(ChevronsDown, { size: 10 }) })] })), onDeleteObject && (_jsx("button", { type: "button", className: "tgo-mini tgo-mini-danger", title: "Remove this drawing \u2014 \u2318Z brings it back", onClick: () => removeMark(o), children: _jsx(Trash2, { size: 10 }) }))] }), onUpdateObject && (_jsx("span", { className: "tgo-resize tgo-resize-se", role: "presentation", title: "Resize", onPointerDown: (e) => startResize(e, 'frame', o.id, 'both'), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer }))] }, o.id));
    };
    const FRAME_PAD = 28;
    const FRAME_BAR = 30;
    // ── S4: ink and shapes ────────────────────────────────────────────────
    //
    // Both land as `canvas_objects` — kinds `ink` and `shape` were declared
    // in the S3 migration and never used, so this needs a renderer and not
    // a schema change, the same way the photo backdrop did.
    /** Stroke thickness in world units, so it stays the same weight on the
     *  board however far you are zoomed out. */
    const INK_WEIGHT = 3;
    /** Enough room around the stroke that a thick line is not clipped by
     *  its own bounding box. */
    const INK_PAD = 8;
    /**
     * Land a freehand stroke.
     *
     * The points are stored RELATIVE to the object's own box, not in world
     * coordinates. Dragging the stroke then moves one row's x/y instead of
     * rewriting every point, resizing is a viewBox scale the browser does
     * for free, and the path never has to be re-normalised on read.
     */
    /** Repaint a mark. Same palette as everything else on the board. */
    const paintMark = useCallback((o, c) => {
        if (!onUpdateObject)
            return;
        const back = { color: o.color ?? null };
        withUndo({ id: o.id, label: 'paint a drawing', undo: () => onUpdateObject(o.id, back) }, () => onUpdateObject(o.id, { color: o.color === c ? null : c }));
    }, [onUpdateObject, withUndo]);
    /** Solid -> outline -> wash -> solid. One control, three states, because
     *  three separate buttons for one property is three times the chrome. */
    const cycleFill = useCallback((o, fill) => {
        if (!onUpdateObject)
            return;
        const next = fill === 'solid' ? 'outline' : fill === 'outline' ? 'wash' : 'solid';
        const back = { content: { ...(o.content || {}) } };
        withUndo({ id: o.id, label: 'change the fill', undo: () => onUpdateObject(o.id, back) }, () => onUpdateObject(o.id, { content: { ...(o.content || {}), fill: next } }));
    }, [onUpdateObject, withUndo]);
    /** Behind the thinking, or over it. */
    const flipDepth = useCallback((o) => {
        if (!onUpdateObject)
            return;
        const from = o.z ?? 0;
        const to = from < 0 ? INFRONT : BEHIND;
        withUndo({ id: o.id, label: to < 0 ? 'send it behind' : 'bring it in front', undo: () => onUpdateObject(o.id, { z: from }) }, () => onUpdateObject(o.id, { z: to }));
    }, [onUpdateObject, withUndo]);
    /** Delete a mark, undoably — the row is gone, so undo re-creates it. */
    const removeMark = useCallback((o) => {
        if (!onDeleteObject)
            return;
        const back = {
            kind: o.kind, x: o.x, y: o.y, w: o.w, h: o.h, z: o.z ?? 0,
            color: o.color ?? null, content: o.content || {},
        };
        withUndo({
            id: o.id,
            label: 'remove a drawing',
            // A hard delete cannot be undone in place: the row is gone and a
            // new one gets a new id. Re-creating it is the honest inverse,
            // and it is why this entry carries no redo.
            undo: () => (onCreateObject ? Promise.resolve(onCreateObject(back)) : Promise.resolve()),
        }, () => onDeleteObject(o.id));
    }, [onDeleteObject, onCreateObject, withUndo]);
    const commitInk = useCallback((pts) => {
        if (!onCreateObject)
            return;
        // Two points a few pixels apart is a click that slipped, not a mark.
        if (pts.length < 2)
            return;
        const b = boundsOf(pts.map((p) => ({ x: p.x, y: p.y, w: 0, h: 0 })));
        if (!b)
            return;
        const x = b.minX - INK_PAD;
        const y = b.minY - INK_PAD;
        const w = Math.max(1, (b.maxX - b.minX) + INK_PAD * 2);
        const h = Math.max(1, (b.maxY - b.minY) + INK_PAD * 2);
        if (w < 6 && h < 6)
            return;
        const local = pts.map((p) => ({ x: p.x - x, y: p.y - y }));
        void Promise.resolve(onCreateObject({
            kind: 'ink',
            x: clampWorld(x),
            y: clampWorld(y),
            w,
            h,
            // Behind the thinking by default: the commonest use is sketching a
            // quadrant or underlining a cluster, and ink over the top of a
            // sticky would cover the words you drew it to point at.
            z: BEHIND,
            content: { d: pathFromPoints(local, false), weight: INK_WEIGHT, vb: { w, h } },
        })).catch(() => { });
    }, [onCreateObject]);
    /** Land a shape dragged out of the board. */
    const commitShape = useCallback((r) => {
        if (!onCreateObject)
            return;
        // A shape smaller than a sticky's corner is a mis-click.
        if (r.w < 12 || r.h < 12)
            return;
        void Promise.resolve(onCreateObject({
            kind: 'shape',
            x: clampWorld(r.x),
            y: clampWorld(r.y),
            w: r.w,
            h: r.h,
            z: BEHIND,
            content: { shape: shapeKind, fill: 'wash' },
        })).catch(() => { });
    }, [onCreateObject, shapeKind]);
    const makeRegion = useCallback(() => {
        if (!onCreateObject || !selectedObjects.length)
            return;
        const b = boundsOf(selectedObjects);
        if (!b)
            return;
        const draft = {
            kind: 'frame',
            x: clampWorld(b.minX - FRAME_PAD),
            y: clampWorld(b.minY - FRAME_PAD - FRAME_BAR),
            w: (b.maxX - b.minX) + FRAME_PAD * 2,
            h: (b.maxY - b.minY) + FRAME_PAD * 2 + FRAME_BAR,
            label: autoTitle(),
        };
        void Promise.resolve(onCreateObject(draft))
            .then((created) => {
            if (created && onDeleteObject) {
                pushUndo({ id: created.id, label: 'make region', undo: () => onDeleteObject(created.id) });
            }
        })
            .catch(() => { });
        sel.clear();
    }, [onCreateObject, onDeleteObject, selectedObjects, autoTitle, pushUndo, sel]);
    /** Save a connector's label. Called by Enter and by clicking away. */
    const commitEdgeLabel = useCallback((e, raw) => {
        setEdgeEditing(null);
        const v = raw.trim();
        if (!onUpdateEdge || v === (e.label || ''))
            return;
        const back = { label: e.label ?? null };
        withUndo({ id: e.id, label: 'label the connection', undo: () => onUpdateEdge(e.id, back) }, () => onUpdateEdge(e.id, { label: v || null }));
    }, [onUpdateEdge, withUndo]);
    /** One geometry pass for connectors: the SVG draws from it and the label
     *  chips sit on it, so a label can never drift off its line. An edge whose
     *  endpoint is not on the board (a paused goal) is simply not drawn. */
    const edgeSegs = useMemo(() => (edges || []).map((e) => {
        const a = anyBox(e.from_kind, e.from_id);
        const b = anyBox(e.to_kind, e.to_id);
        if (!a || !b)
            return null;
        const from = clipToBox(centerOf(b), a, 4);
        const to = clipToBox(from, b);
        return { e, x1: from.x, y1: from.y, x2: to.x, y2: to.y, mx: (from.x + to.x) / 2, my: (from.y + to.y) / 2 };
    }).filter((x) => !!x), [edges, anyBox]);
    /** Text scale steps. Coarse on purpose: a slider invites fiddling, and the
     *  point is three or four legible tiers, not a continuum. */
    const SCALE_STEPS = [1, 1.4, 1.9, 2.6, 3.4];
    const scaleSelection = useCallback((dir) => {
        for (const o of selectedObjects) {
            const row = o.kind === 'goal'
                ? goalById.get(o.id)?.g
                : (notes || []).find((x) => x.id === o.id);
            if (!row)
                continue;
            const cur = row.font_scale || 1;
            // Nearest step, then move one — so a hand-set value from the API still
            // lands somewhere sensible.
            let i = SCALE_STEPS.reduce((best, v, k) => (Math.abs(v - cur) < Math.abs(SCALE_STEPS[best] - cur) ? k : best), 0);
            i = Math.max(0, Math.min(SCALE_STEPS.length - 1, i + dir));
            const next = SCALE_STEPS[i];
            if (next === cur)
                continue;
            if (o.kind === 'goal')
                editGoal(row, { font_scale: next }, 'resize text');
            else
                editNote(row, { font_scale: next }, 'resize text');
        }
    }, [selectedObjects, goalById, notes, editGoal, editNote]);
    /** Arrow-key nudge. Persists once the burst stops so a held key is not one
     *  request per repeat. */
    const nudgeOriginRef = useRef(new Map());
    const nudgeTimerRef = useRef(undefined);
    const flushNudge = useCallback(() => {
        const origin = nudgeOriginRef.current;
        if (!origin.size)
            return;
        const entries = [...origin.entries()];
        nudgeOriginRef.current = new Map();
        const moves = entries.map(([id, from]) => {
            const o = boardById.get(id);
            const at = localPosRef.current[id] || { x: o?.x ?? from.x, y: o?.y ?? from.y };
            return { kind: (o?.kind || 'note'), id, canvas_x: at.x, canvas_y: at.y };
        });
        const back = entries.map(([id, from]) => {
            const o = boardById.get(id);
            return { kind: (o?.kind || 'note'), id, canvas_x: from.x, canvas_y: from.y };
        });
        withUndo({
            label: `nudge ${moves.length} object${moves.length === 1 ? '' : 's'}`,
            ids: entries.map(([id]) => id),
            undo: () => { forgetLocal(entries.map(([id]) => id)); return persistMoves(back); },
        }, () => persistMoves(moves));
    }, [boardById, withUndo, persistMoves, forgetLocal]);
    const nudgeSelection = useCallback((dx, dy) => {
        if (!sel.selectedRef.current.size)
            return;
        setLocalPos((lp) => {
            const next = { ...lp };
            for (const id of sel.selectedRef.current) {
                const o = boardById.get(id);
                if (!o)
                    continue;
                if (!nudgeOriginRef.current.has(id))
                    nudgeOriginRef.current.set(id, { x: o.x, y: o.y });
                // Read the PENDING local position, not the memoised board: a burst of
                // key repeats all lands before React re-renders, and reading the memo
                // made five presses move one pixel (found in the harness).
                const cur = lp[id] || { x: o.x, y: o.y };
                next[id] = { x: clampWorld(cur.x + dx), y: clampWorld(cur.y + dy) };
            }
            return next;
        });
        if (nudgeTimerRef.current)
            clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = setTimeout(flushNudge, 450);
    }, [sel, boardById, flushNudge]);
    /** Connect mode: commit the pending link onto `target`. A sticky attaches
     *  (goal_id); a goal FEEDS the target (parent_goal_id) — the arrow points
     *  at the outcome it serves, like the walk-back chains. */
    const commitLink = (target) => {
        const from = linkFrom;
        setLinkFrom(null);
        if (!from || from.id === target.id)
            return;
        if (from.kind === 'note') {
            const n = (notes || []).find((x) => x.id === from.id);
            if (n && n.goal_id !== target.id)
                editNote(n, { goal_id: target.id }, 'attach thought');
            return;
        }
        const g = goalById.get(from.id)?.g;
        if (!g || g.parent_goal_id === target.id)
            return;
        // Loops are the SERVER's call (it sees paused goals too); a refusal
        // surfaces through the host's toast and records no undo entry.
        editGoal(g, { parent_goal_id: target.id }, `connect “${g.outcome.slice(0, 30)}”`);
    };
    /** Four grab points per object. Hidden until hover, so the board is not a
     *  field of dots — but present on every kind, so any two things can be
     *  joined. Direction comes from geometry; the dot is only where you grab. */
    const anchors = (kind, id) => (onCreateEdge ? (_jsx("span", { className: "tgo-anchors", children: ['n', 'e', 's', 'w'].map((a) => (_jsx("span", { className: `tgo-anchor tgo-anchor-${a}`, role: "presentation", title: "Drag to connect this to something", onPointerDown: (e) => startEdge(e, kind, id), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer }, a))) })) : null);
    /** The swatch row, shared by cards and stickies. */
    const renderPalette = (current, pick) => (_jsxs("span", { className: "tgo-palette", role: "group", "aria-label": "Colour", onPointerDown: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: `tgo-swatch tgo-swatch-none ${!current ? 'tgo-swatch-on' : ''}`, title: "No colour", onClick: () => { setPaletteFor(null); pick(null); }, children: _jsx(X, { size: 9 }) }), CANVAS_COLORS.map((c) => (_jsx("button", { type: "button", className: `tgo-swatch tgo-tone-${c} ${current === c ? 'tgo-swatch-on' : ''}`, title: c, "aria-label": c, onClick: () => { setPaletteFor(null); pick(c); } }, c)))] }));
    /** Fit every object in the viewport (HUD ⤢). */
    const fitAll = useCallback(() => {
        const b = boundsOf(boardObjects);
        if (!b) {
            pz.animateViewTo({ x: 0, y: 0, z: 1 }, 300);
            return;
        }
        pz.fitTo(b);
    }, [boardObjects, pz]);
    // ── throw: Enter = thought (brainstorm-first); the Goal button commits ──
    const throwIt = (asGoal) => {
        const text = seedText.trim();
        if (!text || seedBusy)
            return;
        // Land in the VISIBLE part of the world, near its center, cascading a
        // little per existing object so consecutive throws don't stack.
        const el = viewportRef.current;
        const r = el?.getBoundingClientRect();
        const c = r ? screenToWorld(r.left + r.width * 0.4, r.top + r.height * 0.35)
            : { x: 120, y: 120 };
        // A monotonic slot walk: 6 down, then a new column. The old cascade was
        // `(n % 4)`, so every 4th thought landed exactly on an earlier one and
        // rapid capture buried ideas (canvas review D1).
        const seq = spawnSeq.current++;
        const spawn = {
            canvas_x: clampWorld(c.x + Math.floor(seq / 6) * (NOTE_W + 22)),
            canvas_y: clampWorld(c.y + (seq % 6) * (NOTE_H + 14) + (asGoal ? 60 : 0)),
        };
        const fn = asGoal ? (onCreate && (() => onCreate({ outcome: text, ...spawn })))
            : (onCreateNote && (() => onCreateNote({ text, ...spawn })));
        if (!fn)
            return;
        setSeedBusy(true);
        void Promise.resolve(fn())
            .then(() => setSeedText(''))
            .catch(() => { })
            .finally(() => setSeedBusy(false));
    };
    // ── object drag (a <5px move is a click, not a drag) ─────────────────
    // The whole face of an object is a drag surface — buttons included.
    // Pointer capture is taken only past the threshold so a plain tap still
    // lands its click on the control underneath.
    const startDrag = (e, kind, id, cur) => {
        const el = e.target;
        if (el.closest('input, textarea, select'))
            return; // typing surfaces keep the pointer
        e.stopPropagation(); // never also a pan
        // Shift-click is selection, full stop — it must not reach the control
        // underneath or start a drag.
        if (e.shiftKey) {
            e.preventDefault();
            sel.toggle(id);
            return;
        }
        // Grabbing something already in a multi-selection moves the whole set.
        const inGroup = sel.selectedRef.current.has(id) && sel.selectedRef.current.size > 1;
        const group = inGroup
            ? [...sel.selectedRef.current]
                .map((sid) => boardById.get(sid))
                .filter((o) => !!o)
                .map((o) => ({ id: o.id, kind: o.kind, from: { x: o.x, y: o.y } }))
            : null;
        dragRef.current = {
            kind, id, host: e.currentTarget, pointerId: e.pointerId,
            startX: e.clientX, startY: e.clientY,
            objStart: cur,
            lastX: e.clientX, lastY: e.clientY, moved: false,
            origPos: localPos[id] || null,
            cur: null,
            groupCur: null,
            group,
            tappedControl: !!el.closest('button, a, [role="button"], .tgo-palette'),
        };
    };
    const movePointer = (e) => {
        // Drawing a connector owns the pointer before anything else.
        const ed = edgeRef.current;
        if (ed) {
            const w = screenToWorld(e.clientX, e.clientY);
            const hit = hitAt(w);
            setEdgeDraw({ from: { kind: ed.kind, id: ed.id }, to: w, over: hit && hit.id !== ed.id ? hit.id : null });
            return;
        }
        // A resize owns the pointer before anything else.
        const rz = resizeRef.current;
        if (rz) {
            if (!rz.moved && Math.hypot(e.clientX - rz.startX, e.clientY - rz.startY) < 3)
                return;
            rz.moved = true;
            const z = viewRef.current.z;
            const w = Math.max(MIN_W, Math.min(MAX_W, rz.startW + (e.clientX - rz.startX) / z));
            const h = Math.max(MIN_H, Math.min(MAX_H, rz.startH + (e.clientY - rz.startY) / z));
            rz.curW = w;
            rz.curH = h;
            setLocalSize((p) => ({ ...p, [rz.id]: rz.axis === 'both' ? { w, h } : { w } }));
            return;
        }
        // A region gesture in progress owns the pointer.
        const rg = regionRef.current;
        if (rg) {
            const w = screenToWorld(e.clientX, e.clientY);
            if (rg.kind === 'lasso') {
                rg.pts.push(w);
                setLasso(simplifyPath(rg.pts, 3));
            }
            else if (rg.kind === 'pen') {
                rg.pts.push(w);
                // Simplified as it is drawn, not only at the end: the stroke you
                // watch appear is then the stroke that gets stored, so a line
                // does not subtly change shape the moment you lift the pen.
                setInking(simplifyPath(rg.pts, 2));
            }
            else if (rg.kind === 'shape') {
                rg.pts[1] = w;
                setShaping({ a: rg.pts[0], b: w });
            }
            else {
                rg.pts[1] = w;
                setMarquee({ a: rg.pts[0], b: w });
            }
            return;
        }
        const d = dragRef.current;
        if (!d)
            return;
        d.lastX = e.clientX;
        d.lastY = e.clientY;
        if (!d.moved) {
            if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 5)
                return;
            d.moved = true;
            try {
                d.host.setPointerCapture(d.pointerId);
            }
            catch { /* host unmounted mid-gesture */ }
            setDragging(d.id);
        }
        const z = viewRef.current.z;
        const dx = (e.clientX - d.startX) / z;
        const dy = (e.clientY - d.startY) / z;
        if (d.group) {
            // Every selected object travels by the same delta. A dragged region
            // carries itself too.
            const moved = {};
            for (const m of d.group)
                moved[m.id] = { x: clampWorld(m.from.x + dx), y: clampWorld(m.from.y + dy) };
            if (d.kind === 'frame')
                moved[d.id] = { x: clampWorld(d.objStart.x + dx), y: clampWorld(d.objStart.y + dy) };
            d.groupCur = moved;
            setLocalPos((p) => ({ ...p, ...moved }));
            return;
        }
        const x = clampWorld(d.objStart.x + dx);
        const y = clampWorld(d.objStart.y + dy);
        d.cur = { x, y };
        setLocalPos((p) => ({ ...p, [d.id]: { x, y } }));
        // Live drop-target feedback: the card under a dragged sticky lights up.
        if (d.kind === 'note') {
            let over = null;
            for (const [goalId, cardEl] of cardRefs.current) {
                const cr = cardEl.getBoundingClientRect();
                if (e.clientX >= cr.left && e.clientX <= cr.right && e.clientY >= cr.top && e.clientY <= cr.bottom) {
                    over = goalId;
                    break;
                }
            }
            setDropTarget(over);
        }
    };
    const endPointer = () => {
        // ── a connector lands on whatever is under the pointer ──
        const ed = edgeRef.current;
        if (ed) {
            edgeRef.current = null;
            const drawn = edgeDraw;
            setEdgeDraw(null);
            lastGestureMoved.current = true;
            if (!drawn || !onCreateEdge)
                return;
            const target = hitAt(drawn.to);
            if (!target || (target.kind === ed.kind && target.id === ed.id))
                return;
            const draft = { from_kind: ed.kind, from_id: ed.id, to_kind: target.kind, to_id: target.id, kind: 'arrow' };
            void Promise.resolve(onCreateEdge(draft)).catch(() => { });
            return;
        }
        // ── a resize lands ──
        const rz = resizeRef.current;
        if (rz) {
            resizeRef.current = null;
            lastGestureMoved.current = rz.moved;
            if (!rz.moved)
                return;
            const forget = () => setLocalSize((p) => { const c = { ...p }; delete c[rz.id]; return c; });
            const updates = rz.axis === 'both'
                ? { canvas_w: Math.round(rz.curW), canvas_h: Math.round(rz.curH) }
                : { canvas_w: Math.round(rz.curW) };
            if (rz.kind === 'frame') {
                const f = dragBoxById.get(rz.id);
                if (f && onUpdateObject) {
                    const back = { w: f.w, h: f.h };
                    withUndo({ id: f.id, label: 'resize region', undo: () => { forget(); return onUpdateObject(f.id, back); } }, () => onUpdateObject(f.id, { w: Math.round(rz.curW), h: Math.round(rz.curH) }));
                }
                return;
            }
            if (rz.kind === 'goal') {
                const g = goalById.get(rz.id)?.g;
                if (g && onUpdateGoal) {
                    const back = { canvas_w: g.canvas_w ?? null, canvas_h: g.canvas_h ?? null };
                    withUndo({ id: g.id, label: 'resize goal', undo: () => { forget(); return onUpdateGoal(g.id, back); } }, () => onUpdateGoal(g.id, updates));
                }
            }
            else {
                const n = (notes || []).find((x) => x.id === rz.id);
                if (n && onUpdateNote) {
                    const back = { canvas_w: n.canvas_w ?? null, canvas_h: n.canvas_h ?? null };
                    withUndo({ id: n.id, label: 'resize thought', undo: () => { forget(); return onUpdateNote(n.id, back); } }, () => onUpdateNote(n.id, updates));
                }
            }
            return;
        }
        // ── a region gesture resolves into a selection ──
        const rg = regionRef.current;
        if (rg) {
            regionRef.current = null;
            const pts = rg.pts;
            if (rg.kind === 'pen') {
                setInking(null);
                lastGestureMoved.current = true;
                commitInk(simplifyPath(pts, 2));
                return;
            }
            if (rg.kind === 'shape') {
                setShaping(null);
                lastGestureMoved.current = true;
                commitShape(rectFromDrag(pts[0], pts[1] || pts[0]));
                return;
            }
            const hits = rg.kind === 'lasso'
                ? lassoHits(simplifyPath(pts, 3), boardObjects)
                : marqueeHits(rectFromDrag(pts[0], pts[1] || pts[0]), boardObjects);
            setMarquee(null);
            setLasso(null);
            // A click on empty board with nothing caught clears the selection.
            if (rg.additive)
                sel.addMany(hits);
            else
                sel.setSelection(hits);
            lastGestureMoved.current = hits.length > 0;
            return;
        }
        const d = dragRef.current;
        dragRef.current = null;
        setDragging(null);
        setDropTarget(null);
        lastGestureMoved.current = !!d?.moved;
        // A tap on an object selects it (controls keep their own click).
        if (d && !d.moved) {
            if (!d.tappedControl)
                sel.select(d.id);
            return;
        }
        if (!d)
            return;
        // ── a region drag: the frame's own row, plus its contents ──
        if (d.kind === 'frame') {
            const f = dragBoxById.get(d.id);
            const at = d.groupCur?.[d.id];
            if (!f || !at || !onUpdateObject)
                return;
            const moves = (d.group || []).map((m) => {
                const q = d.groupCur?.[m.id] || m.from;
                return { kind: m.kind, id: m.id, canvas_x: q.x, canvas_y: q.y };
            });
            const back = (d.group || []).map((m) => ({ kind: m.kind, id: m.id, canvas_x: m.from.x, canvas_y: m.from.y }));
            const ids = [d.id, ...(d.group || []).map((m) => m.id)];
            const n = moves.length;
            withUndo({
                label: n ? `move region and ${n} object${n === 1 ? '' : 's'}` : 'move region',
                ids,
                undo: () => { forgetLocal(ids); return Promise.all([onUpdateObject(d.id, { x: d.objStart.x, y: d.objStart.y }), persistMoves(back)]); },
            }, () => Promise.all([onUpdateObject(d.id, { x: at.x, y: at.y }), persistMoves(moves)]));
            return;
        }
        // ── a group drag lands as one batch and one undo entry ──
        if (d.group) {
            const moves = d.group.map((m) => {
                const p = d.groupCur?.[m.id] || localPos[m.id] || m.from;
                return { kind: m.kind, id: m.id, canvas_x: p.x, canvas_y: p.y };
            });
            const back = d.group.map((m) => ({ kind: m.kind, id: m.id, canvas_x: m.from.x, canvas_y: m.from.y }));
            const ids = d.group.map((m) => m.id);
            withUndo({
                label: `move ${moves.length} objects`,
                ids,
                undo: () => { forgetLocal(ids); return persistMoves(back); },
            }, () => persistMoves(moves));
            return;
        }
        const p = d.cur || localPos[d.id];
        // A sticky released over a goal card ATTACHES to it (vision §2) — and
        // STAYS on the board where it was dropped, tethered.
        if (d.kind === 'note' && onUpdateNote) {
            for (const [goalId, cardEl] of cardRefs.current) {
                const r = cardEl.getBoundingClientRect();
                if (d.lastX >= r.left && d.lastX <= r.right && d.lastY >= r.top && d.lastY <= r.bottom) {
                    const n = (notes || []).find((x) => x.id === d.id);
                    const updates = { goal_id: goalId, ...(p ? { canvas_x: p.x, canvas_y: p.y } : {}) };
                    if (linkFrom?.id === d.id)
                        setLinkFrom(null);
                    const before = { goal_id: n?.goal_id ?? null, canvas_x: d.objStart.x, canvas_y: d.objStart.y };
                    withUndo({ id: d.id, label: 'attach thought', undo: () => { setLocalPos((lp) => { const c = { ...lp }; delete c[d.id]; return c; }); return onUpdateNote(d.id, before); } }, () => onUpdateNote(d.id, updates));
                    return;
                }
            }
        }
        if (!p)
            return;
        // A move's inverse puts the object back where the gesture began; the
        // local override is dropped so the persisted position paints again.
        const back = { canvas_x: d.objStart.x, canvas_y: d.objStart.y };
        const forget = () => setLocalPos((lp) => { const c = { ...lp }; delete c[d.id]; return c; });
        if (d.kind === 'goal' && onUpdateGoal) {
            withUndo({ id: d.id, label: 'move goal', undo: () => { forget(); return onUpdateGoal(d.id, back); } }, () => onUpdateGoal(d.id, { canvas_x: p.x, canvas_y: p.y }));
        }
        if (d.kind === 'note' && onUpdateNote) {
            withUndo({ id: d.id, label: 'move thought', undo: () => { forget(); return onUpdateNote(d.id, back); } }, () => onUpdateNote(d.id, { canvas_x: p.x, canvas_y: p.y }));
        }
    };
    // An aborted gesture (incoming call, palm rejection) puts the object back
    // where it started — without this, the card ghost-follows the next hover.
    /** Drag from an anchor dot to any other object. Harvested shape from the
     *  Zero Trust editor's `useEdgeCreation`, which had the only
     *  drag-from-anchor-with-preview in the product. */
    const startEdge = (e, kind, id) => {
        e.stopPropagation();
        e.preventDefault();
        edgeRef.current = { kind, id, pointerId: e.pointerId };
        setEdgeDraw({ from: { kind, id }, to: screenToWorld(e.clientX, e.clientY), over: null });
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch { /* fine */ }
    };
    /** The object under a world point — connectors land on whatever is there,
     *  regions included. */
    const hitAt = useCallback((w) => {
        for (const o of boardObjects) {
            if (w.x >= o.x && w.x <= o.x + o.w && w.y >= o.y && w.y <= o.y + o.h)
                return { kind: o.kind, id: o.id };
        }
        for (const f of frames) {
            if (w.x >= f.x && w.x <= f.x + f.w && w.y >= f.y && w.y <= f.y + f.h)
                return { kind: 'object', id: f.id };
        }
        return null;
    }, [boardObjects, frames]);
    const MIN_W = 120;
    const MAX_W = 1200;
    const MIN_H = 48;
    const MAX_H = 1200;
    /** Grab a corner (thoughts: width AND height) or a right edge (goals:
     *  width only — a card's height is its content, never a preference). */
    const startResize = (e, kind, id, axis) => {
        e.stopPropagation();
        e.preventDefault();
        const box = kind === 'frame' ? dragBoxById.get(id) : boardById.get(id);
        if (!box)
            return;
        resizeRef.current = {
            id, kind, axis, pointerId: e.pointerId, host: e.currentTarget,
            startX: e.clientX, startY: e.clientY, startW: box.w, startH: box.h, moved: false,
            curW: box.w, curH: box.h,
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch { /* fine */ }
    };
    /** Double-click a handle to hand the size back to the content. */
    const resetSize = (kind, id) => {
        setLocalSize((p) => { const c = { ...p }; delete c[id]; return c; });
        if (kind === 'frame')
            return; // a region has no content to fall back to
        const updates = { canvas_w: null, canvas_h: null };
        if (kind === 'goal') {
            const g = goalById.get(id)?.g;
            if (g)
                editGoal(g, updates, 'reset size');
        }
        else {
            const n = (notes || []).find((x) => x.id === id);
            if (n)
                editNote(n, updates, 'reset size');
        }
    };
    /** Background drag with a mouse begins a marquee (or a lasso, when that
     *  tool is up). Pan already declined this pointer in `shouldPan`. */
    const startRegion = (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0)
            return;
        if (spaceRef.current)
            return;
        const el = e.target;
        const onBackground = el === e.currentTarget
            || el.classList.contains('tgo-world')
            || !!el.closest('.tgo-blank');
        if (!onBackground)
            return;
        const w = screenToWorld(e.clientX, e.clientY);
        regionRef.current = {
            pointerId: e.pointerId,
            additive: e.shiftKey,
            kind: tool === 'lasso' ? 'lasso' : tool === 'pen' ? 'pen' : tool === 'shape' ? 'shape' : 'marquee',
            pts: [w, w],
        };
        if (tool === 'lasso')
            setLasso([w]);
        else if (tool === 'pen')
            setInking([w]);
        else if (tool === 'shape')
            setShaping({ a: w, b: w });
        else
            setMarquee({ a: w, b: w });
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch { /* fine */ }
    };
    const cancelPointer = () => {
        edgeRef.current = null;
        setEdgeDraw(null);
        const rz = resizeRef.current;
        resizeRef.current = null;
        if (rz)
            setLocalSize((p) => { const c = { ...p }; delete c[rz.id]; return c; });
        regionRef.current = null;
        setMarquee(null);
        setLasso(null);
        setInking(null);
        setShaping(null);
        const d = dragRef.current;
        dragRef.current = null;
        setDragging(null);
        setDropTarget(null);
        lastGestureMoved.current = !!d?.moved;
        if (!d || !d.moved)
            return;
        setLocalPos((p) => {
            const n = { ...p };
            if (d.origPos)
                n[d.id] = d.origPos;
            else
                delete n[d.id];
            return n;
        });
    };
    // ── facet editing ──────────────────────────────────────────────────────
    const openFacet = (g, facet) => {
        setEditing({ id: g.id, facet });
        setDraft(facet === 'outcome' ? g.outcome
            : facet === 'S' ? (g.account_label || '')
                : facet === 'M' ? (g.measure || '')
                    : facet === 'R' ? (g.why || '')
                        : facet === 'T' ? (g.horizon ? g.horizon.slice(0, 10) : '')
                            : '');
    };
    const commitFacet = (g) => {
        if (!editing || !onUpdateGoal) {
            setEditing(null);
            return;
        }
        const v = draft.trim();
        const updates = editing.facet === 'outcome' ? { outcome: v || g.outcome }
            : editing.facet === 'S' ? { account: v || null }
                : editing.facet === 'M' ? { measure: v || null }
                    : editing.facet === 'R' ? { why: v || null }
                        : editing.facet === 'T' ? { horizon: v || null }
                            : editing.facet === 'person' ? (v ? {
                                people: [
                                    ...(g.people || []),
                                    {
                                        name: v,
                                        ...(people?.find((pp) => pp.name === v)?.email
                                            ? { email: people.find((pp) => pp.name === v).email } : {}),
                                    },
                                ],
                            } : {})
                                : {};
        setEditing(null);
        if (Object.keys(updates).length)
            editGoal(g, updates, `edit ${editing.facet === 'outcome' ? 'outcome' : editing.facet === 'person' ? 'people' : `${editing.facet} facet`}`);
    };
    const facetKeys = (g) => (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            commitFacet(g);
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(null);
        }
    };
    const decompose = (g) => {
        if (!onDecomposeGoal || decomposing)
            return;
        setDecomposing(g.id);
        void Promise.resolve(onDecomposeGoal(g.id)).finally(() => setDecomposing(null));
    };
    /** Re-decompose diffing (vision §5): done steps stay, open steps are
     *  replaced — armed so a stray click can't redraw a plan. */
    const armRedecompose = (g) => {
        if (redecomposeArmed === g.id) {
            setRedecomposeArmed(null);
            decompose(g);
        }
        else {
            setRedecomposeArmed(g.id);
            window.setTimeout(() => setRedecomposeArmed((a) => (a === g.id ? null : a)), 3000);
        }
    };
    const theOne = goals.filter((g) => (g.priority || 0) === 1).length;
    /** Open the precursor input to the LEFT of the chain's tail (or the goal). */
    const openWalkInput = (goalId) => {
        const chain = chains.get(goalId) || [];
        const tail = chain[chain.length - 1];
        const gi = goals.findIndex((g) => g.id === goalId);
        const anchor = tail ? tail.p
            : (gi >= 0 ? posOf(goals[gi].id, goals[gi].canvas_x, goals[gi].canvas_y, gi) : { x: 0, y: 0 });
        setCheckDraft('');
        const wx = clampWorld(anchor.x - (NOTE_W + 70));
        const wy = clampWorld(tail ? anchor.y : anchor.y + 26);
        setWalkFor({ goalId, x: wx, y: wy, order: (tail?.n.walk_order || 0) + 1 });
        // The question must be visible to be answered: pan the input into view
        // when the chain has marched past the viewport's left edge.
        const el = viewportRef.current;
        if (el) {
            const r = el.getBoundingClientRect();
            const v = viewRef.current;
            const screenX = wx * v.z + v.x;
            if (screenX < 16 || screenX + NOTE_W * v.z > r.width - 16) {
                setView({ ...v, x: 40 - wx * v.z });
            }
        }
    };
    const commitWalk = () => {
        const w = walkFor;
        const text = walkDraft.trim();
        setWalkFor(null);
        setCheckDraft('');
        if (!w || !text || !onCreateNote)
            return;
        void Promise.resolve(onCreateNote({
            text, goal_id: w.goalId, walk_order: w.order, canvas_x: w.x, canvas_y: w.y,
        }));
    };
    const suggestWalk = () => {
        const w = walkFor;
        if (!w || !onSuggestWalkBack || walkBusy)
            return;
        setWalkBusy(true);
        void Promise.resolve(onSuggestWalkBack(w.goalId))
            .then((s) => { if (s)
            setCheckDraft(s); })
            .catch(() => { })
            .finally(() => setWalkBusy(false));
    };
    // ── deal-chip drag (dock tray): pot↔bench commits; a chip dropped on a
    // goal card LINKS the deal to that goal — money meets plan without a form.
    // Self-contained pointer machinery: capture keeps every event on the chip,
    // so the board's pan/drag handlers stay quiet.
    const startDealDrag = (e, deal) => {
        dealDragRef.current = {
            deal, pointerId: e.pointerId, host: e.currentTarget,
            startX: e.clientX, startY: e.clientY, moved: false,
        };
        // Capture at DOWN, not at the threshold: a chip has no inner controls to
        // protect, and a fast pointer can leave the chip before any move event
        // lands on it — without capture the drop is silently lost.
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch { /* fine */ }
    };
    const moveDealDrag = (e) => {
        const d = dealDragRef.current;
        const el = viewportRef.current;
        if (!d || !el)
            return;
        if (!d.moved) {
            if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 5)
                return;
            d.moved = true;
        }
        const r = el.getBoundingClientRect();
        setDealGhost({ deal: d.deal, x: e.clientX - r.left, y: e.clientY - r.top });
        // Zone feedback: pot / bench rows, or a goal card in the world.
        const inRect = (ref) => {
            const b = ref.current?.getBoundingClientRect();
            return !!b && e.clientX >= b.left && e.clientX <= b.right && e.clientY >= b.top && e.clientY <= b.bottom;
        };
        setDockZone(inRect(potRef) ? 'pot' : inRect(benchRef) ? 'bench' : null);
        let over = null;
        for (const [goalId, cardEl] of cardRefs.current) {
            const cr = cardEl.getBoundingClientRect();
            if (e.clientX >= cr.left && e.clientX <= cr.right && e.clientY >= cr.top && e.clientY <= cr.bottom) {
                over = goalId;
                break;
            }
        }
        setDropTarget(over);
    };
    const endDealDrag = (e) => {
        const d = dealDragRef.current;
        dealDragRef.current = null;
        const wasZone = dockZone;
        setDealGhost(null);
        setDockZone(null);
        if (!d)
            return;
        if (!d.moved) {
            if (onOpenNumber)
                onOpenNumber();
            return;
        } // a tap is a click-through
        setDropTarget(null);
        if (!onUpdateDeal)
            return;
        // Priority: a goal card under the pointer wins (linking), then the rows.
        for (const [goalId, cardEl] of cardRefs.current) {
            const cr = cardEl.getBoundingClientRect();
            if (e.clientX >= cr.left && e.clientX <= cr.right && e.clientY >= cr.top && e.clientY <= cr.bottom) {
                if (d.deal.goal_id !== goalId)
                    void Promise.resolve(onUpdateDeal(d.deal.id, { goal_id: goalId }));
                return;
            }
        }
        if (wasZone === 'pot' && !d.deal.in_quota)
            void Promise.resolve(onUpdateDeal(d.deal.id, { in_quota: true }));
        if (wasZone === 'bench' && d.deal.in_quota)
            void Promise.resolve(onUpdateDeal(d.deal.id, { in_quota: false }));
    };
    // The board's keyboard. Everything here is guarded on not-typing so a
    // sticky editor keeps its own keys.
    useEffect(() => {
        const onKey = (e) => {
            const t = e.target;
            const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
            const meta = e.metaKey || e.ctrlKey;
            if (e.code === 'Space' && !typing && !spaceRef.current) {
                spaceRef.current = true; // hold to pan, the canvas-tool convention
                setSpacePan(true);
            }
            if (meta && e.key.toLowerCase() === 'z' && !typing) {
                const wantRedo = e.shiftKey;
                if (wantRedo ? !history.canRedo : !history.canUndo)
                    return;
                e.preventDefault();
                if (wantRedo)
                    redo();
                else
                    undo();
                return;
            }
            if (meta && e.key.toLowerCase() === 'y' && !typing) {
                if (!history.canRedo)
                    return;
                e.preventDefault();
                redo();
                return;
            }
            if (meta && e.key.toLowerCase() === 'a' && !typing) {
                e.preventDefault();
                sel.setSelection(boardObjects.map((o) => o.id));
                return;
            }
            if (meta && e.key.toLowerCase() === 'd' && !typing) {
                if (!selectedNotes.length)
                    return;
                e.preventDefault();
                duplicateSelection();
                return;
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') && !typing) {
                if (!selectedNotes.length)
                    return;
                e.preventDefault();
                deleteSelection();
                return;
            }
            if (!typing && !meta && (e.key === 'v' || e.key === 'V')) {
                setTool('select');
                return;
            }
            if (!typing && !meta && (e.key === 'l' || e.key === 'L')) {
                setTool('lasso');
                return;
            }
            if (!typing && !meta && (e.key === 'p' || e.key === 'P') && onCreateObject) {
                setTool('pen');
                return;
            }
            if (!typing && !meta && (e.key === 's' || e.key === 'S') && onCreateObject) {
                setTool('shape');
                return;
            }
            // Escape puts a drawing tool down. Without this the only way back to
            // selecting is to find the HUD, and a pen you cannot put down turns
            // every stray click into a mark.
            if (!typing && e.key === 'Escape' && (tool === 'pen' || tool === 'shape')) {
                setTool('select');
                return;
            }
            if (!typing && !meta && e.key.startsWith('Arrow') && sel.selectedRef.current.size) {
                e.preventDefault();
                const step = e.shiftKey ? 10 : 1;
                nudgeSelection(e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0, e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0);
                return;
            }
            if (e.key === 'Escape' && !typing) {
                if (composeAt) {
                    setComposeAt(null);
                    return;
                }
                if (linkFrom) {
                    setLinkFrom(null);
                    return;
                }
                if (paletteFor) {
                    setPaletteFor(null);
                    return;
                }
                if (sel.selectedRef.current.size) {
                    sel.clear();
                    return;
                }
                if (full)
                    setFull(false);
            }
        };
        const onKeyUp = (e) => {
            if (e.code === 'Space') {
                spaceRef.current = false;
                setSpacePan(false);
            }
        };
        // A lost focus must not leave space stuck down.
        const onBlur = () => { spaceRef.current = false; setSpacePan(false); };
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, [
        undo, redo, history.canUndo, history.canRedo, linkFrom, paletteFor, full, composeAt,
        sel, boardObjects, selectedNotes, duplicateSelection, deleteSelection, nudgeSelection,
        // Escape's meaning depends on which tool is in hand, so a stale
        // closure here would leave the pen down after you pressed it.
        tool, onCreateObject,
    ]);
    // Paste a list and get a sticky per line — the fastest way to get a
    // meeting's raw notes onto the board.
    useEffect(() => {
        if (!onCreateNote)
            return undefined;
        const onPaste = (e) => {
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
                return;
            if (!viewportRef.current?.isConnected)
                return;
            const text = e.clipboardData?.getData('text/plain') || '';
            const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).slice(0, 50);
            if (lines.length < 1)
                return;
            e.preventDefault();
            const r = viewportRef.current.getBoundingClientRect();
            const anchor = screenToWorld(r.left + r.width * 0.32, r.top + r.height * 0.22);
            void Promise.all(lines.map((line, i) => onCreateNote({
                text: line,
                canvas_x: clampWorld(anchor.x + Math.floor(i / 8) * (NOTE_W + 24)),
                canvas_y: clampWorld(anchor.y + (i % 8) * (NOTE_H + 14)),
            })));
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [onCreateNote, screenToWorld, viewportRef]);
    // ── note renderer (shared by loose + attached + walk-back stickies) ────
    const renderNote = (n, p, isAttached) => {
        const isEd = editing?.id === n.id && editing.facet === 'note';
        const isWalk = n.walk_order != null;
        const chain = isWalk ? chains.get(n.goal_id) : undefined;
        const isTail = !!chain && chain[chain.length - 1]?.n.id === n.id;
        return (_jsxs("div", { ref: objRef(n.id, false), className: `tgo-note ${toneClass(n.color)} ${isAttached ? 'tgo-note-attached' : ''} ${isWalk ? 'tgo-note-walk' : ''} ${dragging === n.id ? 'tgo-dragging' : ''} ${linkFrom?.id === n.id ? 'tgo-link-source' : ''} ${sel.selected.has(n.id) ? 'tgo-selected' : ''} ${edgeDraw?.over === n.id ? 'tgo-edge-target' : ''} ${(localSize[n.id]?.h ?? n.canvas_h) != null ? 'tgo-note-sized' : ''} ${n.needs_review ? 'tgo-note-unsure' : ''} ${checkAt?.id === n.id ? 'tgo-note-checking' : ''}`, style: {
                left: p.x,
                top: p.y,
                width: localSize[n.id]?.w ?? n.canvas_w ?? undefined,
                height: localSize[n.id]?.h ?? n.canvas_h ?? undefined,
                ...(n.font_scale && n.font_scale !== 1 ? { '--tgo-fs': n.font_scale } : {}),
            }, onPointerDown: (e) => startDrag(e, 'note', n.id, p), title: isWalk
                ? `Walk-back step ${n.walk_order} — must be true before ${n.walk_order === 1 ? 'the outcome' : 'the next step'}`
                : isAttached ? 'Part of its goal\'s brainstorm — drag to move, unlink to release' : 'Drag onto a goal to attach it to that goal\'s brainstorm', children: [isWalk && (_jsx("span", { className: "tgo-walk-badge", title: `${n.walk_order} back from the outcome`, children: n.walk_order })), n.needs_review && (_jsx("span", { className: "tgo-unsure-badge", title: `Read off the photo${typeof n.source_ref?.legibility === 'number'
                        ? ` with ${Math.round(n.source_ref.legibility * 100)}% confidence`
                        : ''} and not checked yet. Compare it with the photo underneath, then mark it checked.`, children: _jsx(HelpCircle, { size: 10 }) })), isTail && onCreateNote && !walkFor && (_jsx("button", { type: "button", className: "tgo-walk-btn", title: "Keep walking back \u2014 what must be true before THIS?", onClick: () => openWalkInput(n.goal_id), children: _jsx(ArrowLeft, { size: 11 }) })), isEd ? (_jsx("textarea", { className: "tgo-edit tgo-edit-note", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => {
                        setEditing(null);
                        const v = draft.trim();
                        if (v && v !== n.text && onUpdateNote)
                            editNote(n, { text: v }, 'edit thought');
                    }, onKeyDown: (e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            e.target.blur();
                        }
                        if (e.key === 'Escape')
                            setEditing(null);
                    } })) : (_jsx("button", { type: "button", className: "tgo-note-text", onClick: () => { if (onUpdateNote) {
                        setEditing({ id: n.id, facet: 'note' });
                        setDraft(n.text);
                    } }, title: "Click to edit", children: n.text })), _jsxs("span", { className: "tgo-note-acts", children: [isAttached && onUpdateNote && (_jsx("button", { type: "button", className: "tgo-mini", title: "Release this thought from its goal", onClick: () => editNote(n, { goal_id: null }, 'release thought'), children: _jsx(Unlink, { size: 10 }) })), !isAttached && onUpdateNote && goals.length > 0 && (_jsx("button", { type: "button", className: `tgo-mini ${linkFrom?.id === n.id ? 'tgo-mini-armed' : ''}`, title: linkFrom?.id === n.id ? 'Now click the goal to attach it to (Esc cancels)' : 'Connect — click, then click a goal to attach this thought', onClick: () => setLinkFrom(linkFrom?.id === n.id ? null : { kind: 'note', id: n.id }), children: _jsx(Link2, { size: 10 }) })), onUpdateNote && (_jsx("button", { type: "button", className: `tgo-mini ${paletteFor === n.id ? 'tgo-mini-armed' : ''}`, title: "Colour", onClick: () => setPaletteFor(paletteFor === n.id ? null : n.id), children: _jsx(Palette, { size: 10 }) })), !isAttached && onCreate && (_jsx("button", { type: "button", className: "tgo-mini", title: "Promote this thought into a goal", onClick: () => {
                                // The goal is born where the thought lived (review m5).
                                void Promise.resolve(onCreate({ outcome: n.text, canvas_x: p.x, canvas_y: p.y }))
                                    .then(() => { if (onDeleteNote)
                                    return Promise.resolve(onDeleteNote(n.id)).then(() => retireUndo(n.id)); return undefined; })
                                    .catch(() => { });
                            }, children: _jsx(Target, { size: 10 }) })), onDeleteNote && (noteArmed === n.id ? (_jsx("button", { type: "button", className: "tgo-mini tgo-mini-danger", title: "Confirm delete", onClick: () => { setNoteArmed(null); removeNote(n, p); }, children: "sure?" })) : (_jsx("button", { type: "button", className: "tgo-mini", title: "Delete thought", onClick: () => setNoteArmed(n.id), children: _jsx(Trash2, { size: 10 }) })))] }), paletteFor === n.id && onUpdateNote && renderPalette(n.color, (c) => editNote(n, { color: c }, 'recolour thought')), anchors('note', n.id), onUpdateNote && (_jsx("span", { className: "tgo-resize tgo-resize-se", role: "presentation", title: "Drag to resize \u00B7 double-click to fit the text again", onPointerDown: (e) => startResize(e, 'note', n.id, 'both'), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer, onDoubleClick: (e) => { e.stopPropagation(); resetSize('note', n.id); } }))] }, n.id));
    };
    const aimShown = goals.length > AIM_SMALL_MAX && (aimDismissed == null || goals.length > aimDismissed);
    const dismissAim = () => {
        setAimDismissed(goals.length);
        try {
            window.localStorage.setItem(AIM_DISMISS_KEY, String(goals.length));
        }
        catch { /* best effort */ }
    };
    return (_jsxs("div", { className: `tgo ${full ? 'tgo-full' : ''}`, children: [_jsxs("div", { className: "tgo-throw", children: [_jsx("input", { className: "tgo-throw-input", placeholder: "Think out loud \u2014 Enter throws a thought on the board; make it a goal when it's ready", value: seedText, onChange: (e) => setSeedText(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing)
                            throwIt(!onCreateNote); } }), onCreateNote && (_jsx("button", { type: "button", className: "tgo-throw-go", disabled: !seedText.trim() || seedBusy, onClick: () => throwIt(false), children: seedBusy ? _jsx(Loader2, { size: 13, className: "tgl-spin" }) : _jsxs(_Fragment, { children: [_jsx(Plus, { size: 12 }), " Thought"] }) })), onCreate && (_jsxs("button", { type: "button", className: "tgo-throw-go tgo-throw-goal", disabled: !seedText.trim() || seedBusy, onClick: () => throwIt(true), children: [_jsx(Target, { size: 12 }), " Goal"] })), onImportPhoto && (_jsxs("button", { type: "button", className: "tgo-throw-go", title: "Photograph a real whiteboard and lay it under this board", onClick: () => setPhotoOpen(true), children: [_jsx(ImageIcon, { size: 12 }), " Photo"] })), full && onOpenRegistries && (_jsxs("button", { type: "button", className: "tgo-throw-go", title: "Rhythms & registries \u2014 paused and achieved goals, coverage, syncs, pursuits", onClick: onOpenRegistries, children: [_jsx(PanelRight, { size: 12 }), " Registries"] })), full && (_jsx("button", { type: "button", className: "tgo-throw-go", title: "Leave full page (Esc)", onClick: () => setFull(false), children: _jsx(Shrink, { size: 12 }) }))] }), photoOpen && onImportPhoto && (_jsx(PhotoImport, { onImport: onImportPhoto, onClose: () => setPhotoOpen(false) })), aimShown && (_jsxs("div", { className: "tgo-aim", role: "status", children: [_jsxs("span", { children: [goals.length, " active goals \u2014 aim small, miss small. 3\u20134 critical initiatives maximize a week; park what you won't push this quarter, and flag THE ONE as P1."] }), _jsx("button", { type: "button", className: "tgo-aim-x", title: "Got it \u2014 hide until the count changes", onClick: dismissAim, children: _jsx(X, { size: 11 }) })] })), linkFrom && (_jsxs("div", { className: "tgo-aim tgo-aim-soft tgo-aim-link", role: "status", children: [_jsx(Link2, { size: 11 }), " ", linkFrom.kind === 'note' ? 'Click the goal this thought belongs to' : 'Click the goal this one feeds', " \u2014 Esc cancels.", _jsx("button", { type: "button", className: "tgo-aim-x", title: "Cancel", onClick: () => setLinkFrom(null), children: _jsx(X, { size: 11 }) })] })), goals.length > 1 && theOne === 0 && goals.length <= AIM_SMALL_MAX && (_jsx("div", { className: "tgo-aim tgo-aim-soft", role: "status", children: "No P1 yet \u2014 which ONE of these would blow out the number? Flag it and give it 80% of the week." })), _jsxs("div", { className: `tgo-board ${panning ? 'tgo-panning' : ''} ${linkFrom ? 'tgo-linking' : ''} ${spacePan ? 'tgo-spacepan' : ''} ${tool === 'lasso' ? 'tgo-lassomode' : ''} ${tool === 'pen' ? 'tgo-penmode' : ''} ${tool === 'shape' ? 'tgo-shapemode' : ''}`, ref: viewportRef, style: {
                    backgroundSize: `${22 * view.z}px ${22 * view.z}px`,
                    backgroundPosition: `${view.x}px ${view.y}px`,
                }, onPointerDown: (e) => { pz.handlers.onPointerDown(e); startRegion(e); }, onPointerMove: (e) => { pz.handlers.onPointerMove(e); movePointer(e); }, onPointerUp: (e) => { pz.handlers.onPointerUp(e); endPointer(); }, onPointerCancel: (e) => { pz.handlers.onPointerCancel(e); cancelPointer(); }, onDoubleClick: (e) => {
                    const el = e.target;
                    if (!(el === e.currentTarget || el.classList.contains('tgo-world') || el.closest('.tgo-blank')))
                        return;
                    if (!onCreateNote)
                        return;
                    setComposeText('');
                    setComposeAt(screenToWorld(e.clientX, e.clientY));
                }, role: "region", "aria-label": "Goal whiteboard", children: [goals.length === 0 && (notes || []).length === 0 && (_jsxs("div", { className: "tgo-blank", children: [_jsx("p", { children: "This is your whiteboard." }), _jsxs("p", { children: ["Every other view sorts work that ", _jsx("em", { children: "arrives" }), " \u2014 this board holds the work you ", _jsx("strong", { children: "choose" }), ". Think out loud: throw thoughts, drag them around, drop them on a goal, then run the decomposition."] })] })), _jsxs("div", { className: "tgo-world", style: { transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})` }, children: [(() => {
                                const segs = [];
                                // Free connectors (S3): untyped, labelled by the human.
                                for (const seg of edgeSegs) {
                                    segs.push({
                                        id: `edge-${seg.e.id}`, x1: seg.x1, y1: seg.y1, x2: seg.x2, y2: seg.y2,
                                        walk: false, free: seg.e.kind || 'arrow', tone: seg.e.color,
                                    });
                                }
                                // The one being drawn right now.
                                if (edgeDraw) {
                                    const a = anyBox(edgeDraw.from.kind, edgeDraw.from.id);
                                    if (a) {
                                        const c = centerOf(a);
                                        const start = clipToBox(edgeDraw.to, a, 4);
                                        segs.push({ id: 'edge-draft', x1: start.x, y1: start.y, x2: edgeDraw.to.x, y2: edgeDraw.to.y, walk: false, free: 'draft' });
                                        void c;
                                    }
                                }
                                // Goal → goal: a child card feeds its parent (arrow at the parent).
                                goals.forEach((g) => {
                                    if (!g.parent_goal_id)
                                        return;
                                    const child = boardById.get(g.id);
                                    const parent = boardById.get(g.parent_goal_id);
                                    if (!child || !parent)
                                        return;
                                    const from = clipToBox(centerOf(parent), child, 4);
                                    const to = clipToBox(from, parent);
                                    segs.push({ id: `link-${g.id}`, x1: from.x, y1: from.y, x2: to.x, y2: to.y, walk: false, link: true, tone: g.color });
                                });
                                // Plain brainstorm: note → its goal (dashed, no direction).
                                for (const { n } of attachedPlaced) {
                                    if (n.walk_order != null)
                                        continue;
                                    const nb = boardById.get(n.id);
                                    const gb = n.goal_id ? boardById.get(n.goal_id) : null;
                                    if (!nb || !gb)
                                        continue;
                                    const from = centerOf(nb);
                                    const to = clipToBox(from, gb);
                                    segs.push({ id: n.id, x1: from.x, y1: from.y, x2: to.x, y2: to.y, walk: false });
                                }
                                // Walk-back chains: deepest → … → 1 → goal, arrowheads forward.
                                for (const [goalId, chain] of chains) {
                                    for (let i = 0; i < chain.length; i++) {
                                        const src = chain[i];
                                        const sb = boardById.get(src.n.id);
                                        const dst = i === 0 ? boardById.get(goalId) : boardById.get(chain[i - 1].n.id);
                                        if (!sb || !dst)
                                            continue;
                                        const from = centerOf(sb);
                                        const to = clipToBox(from, dst);
                                        segs.push({ id: `walk-${src.n.id}`, x1: from.x, y1: from.y, x2: to.x, y2: to.y, walk: true });
                                    }
                                }
                                if (!segs.length)
                                    return null;
                                const pad = 20;
                                const minX = Math.min(...segs.flatMap((s) => [s.x1, s.x2])) - pad;
                                const minY = Math.min(...segs.flatMap((s) => [s.y1, s.y2])) - pad;
                                const w = Math.max(...segs.flatMap((s) => [s.x1, s.x2])) + pad - minX;
                                const h = Math.max(...segs.flatMap((s) => [s.y1, s.y2])) + pad - minY;
                                return (_jsxs("svg", { className: "tgo-tethers", "aria-hidden": "true", style: { left: minX, top: minY, width: w, height: h }, viewBox: `${minX} ${minY} ${w} ${h}`, children: [_jsx("defs", { children: _jsx("marker", { id: "tgo-arrow", viewBox: "0 0 8 8", refX: "7", refY: "4", markerWidth: "7", markerHeight: "7", orient: "auto-start-reverse", children: _jsx("path", { d: "M0,0 L8,4 L0,8 z" }) }) }), segs.map((s) => (_jsx("line", { className: `${s.walk ? 'tgo-tether-walk' : ''} ${s.link ? `tgo-tether-link ${s.tone ? `tgo-tone-${s.tone}` : ''}` : ''} ${s.free ? `tgo-tether-free tgo-tether-${s.free} ${s.tone ? `tgo-tone-${s.tone}` : ''}` : ''}`, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2, markerEnd: s.walk || s.link || (s.free && s.free !== 'line') ? 'url(#tgo-arrow)' : undefined }, s.id)))] }));
                            })(), edgeSegs.map((seg) => (_jsx("div", { className: "tgo-edge-label", style: { left: seg.mx, top: seg.my }, children: edgeEditing === seg.e.id ? (_jsx("input", { className: "tgo-edit tgo-edge-input", defaultValue: seg.e.label || '', autoFocus: true, placeholder: "blocks / feeds / because\u2026", onPointerDown: (e) => e.stopPropagation(), onBlur: (e) => {
                                        if (justCommitted.current === seg.e.id) {
                                            justCommitted.current = null;
                                            return;
                                        }
                                        commitEdgeLabel(seg.e, e.target.value);
                                    }, onKeyDown: (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            justCommitted.current = seg.e.id;
                                            commitEdgeLabel(seg.e, e.target.value);
                                        }
                                        if (e.key === 'Escape') {
                                            justCommitted.current = seg.e.id;
                                            setEdgeEditing(null);
                                        }
                                    } })) : (_jsxs("span", { className: "tgo-edge-chip", onPointerDown: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: `tgo-edge-text ${seg.e.label ? '' : 'tgo-edge-empty'}`, title: "Say what this connection means", onClick: () => onUpdateEdge && setEdgeEditing(seg.e.id), children: seg.e.label || '+' }), onDeleteEdge && (_jsx("button", { type: "button", className: "tgo-edge-x", title: "Remove this connection", onClick: () => {
                                                const snap = { from_kind: seg.e.from_kind, from_id: seg.e.from_id, to_kind: seg.e.to_kind, to_id: seg.e.to_id, label: seg.e.label, kind: seg.e.kind, color: seg.e.color };
                                                void Promise.resolve(onDeleteEdge(seg.e.id))
                                                    .then(() => {
                                                    retireUndo(seg.e.id);
                                                    if (onCreateEdge)
                                                        pushUndo({ label: 'remove connection', undo: () => onCreateEdge(snap) });
                                                })
                                                    .catch(() => { });
                                            }, children: _jsx(X, { size: 9 }) }))] })) }, `lbl-${seg.e.id}`))), unattached.map((n, i) => renderNote(n, posOf(n.id, n.canvas_x, n.canvas_y, i, true), false)), attachedPlaced.map(({ n, p }) => renderNote(n, p, true)), backdrops.map((b) => (_jsxs("div", { className: "tgo-shot", style: { left: b.x, top: b.y, width: b.w, height: b.h }, children: [b.signed_url ? (_jsx("img", { src: b.signed_url, alt: b.label || 'Whiteboard photo', draggable: false, 
                                        // While the review walk is running the backdrop comes up
                                        // to full strength: the whole task is comparing a
                                        // transcription against the handwriting, and you cannot
                                        // do that through a 35% ghost.
                                        style: { opacity: checkAt ? 1 : (Number(b.content?.opacity) || 0.35) } })) : (_jsx("div", { className: "tgo-shot-gone", children: b.signed_url_error || 'photo unavailable' })), _jsxs("div", { className: "tgo-shot-bar", onPointerDown: (e) => startFrameDrag(e, b), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer, children: [_jsx("span", { className: "tgo-shot-name", children: b.label || 'Whiteboard photo' }), onUpdateObject && (_jsx("input", { className: "tgo-shot-fade", type: "range", min: 5, max: 100, defaultValue: Math.round((Number(b.content?.opacity) || 0.35) * 100), title: "How strongly the photo shows through", onPointerDown: (e) => e.stopPropagation(), onChange: (e) => {
                                                    const v = Number(e.target.value) / 100;
                                                    const el = elRefs.current.get(b.id)?.querySelector('img');
                                                    if (el)
                                                        el.style.opacity = String(v); // instant, no round trip
                                                }, onPointerUp: (e) => {
                                                    const v = Number(e.target.value) / 100;
                                                    const back = { content: { ...(b.content || {}), opacity: Number(b.content?.opacity) || 0.35 } };
                                                    withUndo({ id: b.id, label: 'fade the photo', undo: () => onUpdateObject(b.id, back) }, () => onUpdateObject(b.id, { content: { ...(b.content || {}), opacity: v } }));
                                                } })), onReadPhoto && (_jsxs("button", { type: "button", className: "tgo-mini tgo-mini-wide", title: "Read the handwriting and propose a sticky for each note. Nothing is added until you choose.", disabled: !!readingId, onClick: () => readShot(b.id), children: [readingId === b.id
                                                        ? _jsx(Loader2, { size: 10, className: "tgl-spin" })
                                                        : _jsx(ScanText, { size: 10 }), "Read stickies"] })), onDeleteObject && (_jsx("button", { type: "button", className: "tgo-mini", title: "Remove the photo \u2014 what you drew on top of it stays", onClick: () => { void Promise.resolve(onDeleteObject(b.id)).catch(() => { }); }, children: _jsx(Trash2, { size: 10 }) }))] }), onUpdateObject && (_jsx("span", { className: "tgo-resize tgo-resize-se", role: "presentation", title: "Resize the photo to match the board's scale", onPointerDown: (e) => startResize(e, 'frame', b.id, 'both'), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer }))] }, b.id))), marks.behind.map(renderMark), frames.map((f) => (_jsxs("div", { className: `tgo-frame ${toneClass(f.color)}`, style: { left: f.x, top: f.y, width: f.w, height: f.h }, children: [_jsxs("div", { className: "tgo-frame-bar", onPointerDown: (e) => startFrameDrag(e, f), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer, children: [editing?.id === f.id && editing.facet === 'frame' ? (_jsx("input", { className: "tgo-edit tgo-frame-edit", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: (e) => {
                                                    if (justCommitted.current === f.id) {
                                                        justCommitted.current = null;
                                                        return;
                                                    }
                                                    commitFrameTitle(f, e.target.value);
                                                }, onKeyDown: (e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        justCommitted.current = f.id;
                                                        commitFrameTitle(f, e.target.value);
                                                    }
                                                    if (e.key === 'Escape') {
                                                        justCommitted.current = f.id;
                                                        setEditing(null);
                                                    }
                                                } })) : (_jsx("button", { type: "button", className: "tgo-frame-title", title: "Click to rename \u00B7 drag to move the region and everything in it", onClick: () => { if (onUpdateObject) {
                                                    setEditing({ id: f.id, facet: 'frame' });
                                                    setDraft(f.label || '');
                                                } }, children: f.label || 'Region' })), _jsxs("span", { className: "tgo-frame-acts", children: [_jsx("span", { className: "tgo-frame-count", children: containedBy(f).length }), onPolishRegion && onUpdateNote && (_jsx("button", { type: "button", className: "tgo-mini", disabled: !!regionBusy, title: "Polish \u2014 tidy the wording of everything in here. Nothing moves, nothing is created, and \u2318Z undoes the whole pass.", onClick: () => polish(f), children: regionBusy?.id === f.id && regionBusy.verb === 'polish'
                                                            ? _jsx(Loader2, { size: 10, className: "tgl-spin" }) : _jsx(Wand2, { size: 10 }) })), onProcessRegion && (_jsxs("button", { type: "button", className: "tgo-mini tgo-mini-wide", disabled: !!regionBusy, title: "Process \u2014 read this region and propose a plan. Nothing is written until you tick the lines you want.", onClick: () => process(f), children: [regionBusy?.id === f.id && regionBusy.verb === 'process'
                                                                ? _jsx(Loader2, { size: 10, className: "tgl-spin" }) : _jsx(ListChecks, { size: 10 }), " Process"] })), onUpdateObject && (_jsx("button", { type: "button", className: `tgo-mini ${paletteFor === f.id ? 'tgo-mini-armed' : ''}`, title: "Colour this region", onClick: () => setPaletteFor(paletteFor === f.id ? null : f.id), children: _jsx(Palette, { size: 10 }) })), onDeleteObject && (_jsx("button", { type: "button", className: "tgo-mini", title: "Remove the region \u2014 what is inside stays exactly where it is", onClick: () => {
                                                            const snapshot = { kind: 'frame', x: f.x, y: f.y, w: f.w, h: f.h, label: f.label, color: f.color };
                                                            void Promise.resolve(onDeleteObject(f.id))
                                                                .then(() => {
                                                                retireUndo(f.id);
                                                                if (onCreateObject)
                                                                    pushUndo({ label: 'remove region', undo: () => onCreateObject(snapshot) });
                                                            })
                                                                .catch(() => { });
                                                        }, children: _jsx(Trash2, { size: 10 }) }))] })] }), paletteFor === f.id && onUpdateObject && (_jsx("div", { className: "tgo-frame-palette", children: renderPalette(f.color, (c) => {
                                            const back = { color: f.color ?? null };
                                            withUndo({ id: f.id, label: 'recolour region', undo: () => onUpdateObject(f.id, back) }, () => onUpdateObject(f.id, { color: c }));
                                        }) })), anchors('object', f.id), onUpdateObject && (_jsx("span", { className: "tgo-resize tgo-resize-se", role: "presentation", title: "Drag to resize the region", onPointerDown: (e) => startResize(e, 'frame', f.id, 'both'), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer }))] }, f.id))), marquee && (() => {
                                const r = rectFromDrag(marquee.a, marquee.b);
                                return _jsx("div", { className: "tgo-marquee", style: { left: r.x, top: r.y, width: r.w, height: r.h } });
                            })(), marks.front.map(renderMark), inking && inking.length > 1 && (() => {
                                const xs = inking.map((q) => q.x);
                                const ys = inking.map((q) => q.y);
                                const pad = 8;
                                const minX = Math.min(...xs) - pad;
                                const minY = Math.min(...ys) - pad;
                                const w = Math.max(...xs) - minX + pad;
                                const h = Math.max(...ys) - minY + pad;
                                return (_jsx("svg", { className: "tgo-inking", "aria-hidden": "true", style: { left: minX, top: minY, width: w, height: h }, viewBox: `${minX} ${minY} ${w} ${h}`, children: _jsx("path", { d: pathFromPoints(inking, false) }) }));
                            })(), shaping && (() => {
                                const r = rectFromDrag(shaping.a, shaping.b);
                                return (_jsx("div", { className: `tgo-shaping tgo-shaping-${shapeKind}`, style: { left: r.x, top: r.y, width: r.w, height: r.h } }));
                            })(), lasso && lasso.length > 1 && (() => {
                                const xs = lasso.map((q) => q.x);
                                const ys = lasso.map((q) => q.y);
                                const pad = 8;
                                const minX = Math.min(...xs) - pad;
                                const minY = Math.min(...ys) - pad;
                                const w = Math.max(...xs) - minX + pad;
                                const h = Math.max(...ys) - minY + pad;
                                return (_jsx("svg", { className: "tgo-lasso", "aria-hidden": "true", style: { left: minX, top: minY, width: w, height: h }, viewBox: `${minX} ${minY} ${w} ${h}`, children: _jsx("path", { d: pathFromPoints(lasso, true) }) }));
                            })(), composeAt && onCreateNote && (_jsx("div", { className: "tgo-note tgo-note-walkinput", style: { left: composeAt.x, top: composeAt.y }, children: _jsx("textarea", { className: "tgo-edit tgo-edit-note", placeholder: "What's the thought?", value: composeText, autoFocus: true, onChange: (e) => setComposeText(e.target.value), onKeyDown: (e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            const text = composeText.trim();
                                            const at = composeAt;
                                            setComposeText('');
                                            // Stay open and step down, so a double-click is the start
                                            // of a run of thoughts rather than a single one.
                                            setComposeAt({ x: at.x, y: clampWorld(at.y + NOTE_H + 14) });
                                            if (text)
                                                void Promise.resolve(onCreateNote({ text, canvas_x: at.x, canvas_y: at.y }));
                                        }
                                        if (e.key === 'Escape') {
                                            setComposeAt(null);
                                            setComposeText('');
                                        }
                                    }, onBlur: () => {
                                        const text = composeText.trim();
                                        if (text)
                                            void Promise.resolve(onCreateNote({ text, canvas_x: composeAt.x, canvas_y: composeAt.y }));
                                        setComposeText('');
                                        setComposeAt(null);
                                    } }) })), walkFor && (_jsxs("div", { className: "tgo-note tgo-note-walkinput", style: { left: walkFor.x, top: walkFor.y }, children: [_jsx("span", { className: "tgo-walk-badge", children: walkFor.order }), _jsx("textarea", { className: "tgo-edit tgo-edit-note", placeholder: "What must be true before this?", value: walkDraft, autoFocus: true, onChange: (e) => setCheckDraft(e.target.value), onKeyDown: (e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                commitWalk();
                                            }
                                            if (e.key === 'Escape') {
                                                setWalkFor(null);
                                                setCheckDraft('');
                                            }
                                        } }), _jsxs("span", { className: "tgo-note-acts tgo-walk-acts", children: [onSuggestWalkBack && (_jsx("button", { type: "button", className: "tgo-mini", disabled: walkBusy, title: "Suggest this step from the account facts and your brainstorm \u2014 you can rewrite it", onClick: suggestWalk, children: walkBusy ? _jsx(Loader2, { size: 10, className: "tgl-spin" }) : _jsx(Sparkles, { size: 10 }) })), _jsx("button", { type: "button", className: "tgo-mini", disabled: !walkDraft.trim(), title: "Add the step (Enter)", onClick: commitWalk, children: _jsx(Check, { size: 10 }) }), _jsx("button", { type: "button", className: "tgo-mini", title: "Cancel (Esc)", onClick: () => { setWalkFor(null); setCheckDraft(''); }, children: _jsx(X, { size: 10 }) })] })] })), goals.map((g, i) => {
                                const goalTasks = tasksByGoal?.get(g.id) || [];
                                const planExists = (g.task_count ?? goalTasks.length) > 1 || goalTasks.some((t) => t.is_project);
                                const letters = smartState(g, planExists);
                                const litCount = Object.values(letters).filter(Boolean).length;
                                const firstUnlit = ['S', 'M', 'A', 'R', 'T'].find((l) => !letters[l]);
                                const maturity = litCount === 5 ? 'smart' : litCount >= 3 ? 'forming' : 'seed';
                                const p = posOf(g.id, g.canvas_x, g.canvas_y, i);
                                const open = goalTasks.filter((t) => t.status !== 'completed' && !t.archived_at).length;
                                const done = goalTasks.filter((t) => t.status === 'completed').length;
                                const priority = g.priority || 0;
                                const gPeople = g.people || [];
                                const isEd = (f) => editing?.id === g.id && editing.facet === f;
                                const busy = decomposing === g.id;
                                const parentOutcome = g.parent_goal_id ? goalById.get(g.parent_goal_id)?.g.outcome.slice(0, 40) : undefined;
                                return (_jsxs("div", { ref: objRef(g.id, true), className: `tgo-card tgo-card-${maturity} ${toneClass(g.color)} ${priority === 1 ? 'tgo-card-one' : ''} ${dragging === g.id ? 'tgo-dragging' : ''} ${dropTarget === g.id ? 'tgo-drop-target' : ''} ${linkFrom?.id === g.id ? 'tgo-link-source' : ''} ${sel.selected.has(g.id) ? 'tgo-selected' : ''} ${edgeDraw?.over === g.id ? 'tgo-edge-target' : ''}`, style: {
                                        left: p.x,
                                        top: p.y,
                                        width: localSize[g.id]?.w ?? g.canvas_w ?? undefined,
                                        ...(g.font_scale && g.font_scale !== 1 ? { '--tgo-fs': g.font_scale } : {}),
                                    }, onPointerDown: (e) => startDrag(e, 'goal', g.id, p), onClick: linkFrom && linkFrom.id !== g.id ? () => { if (!lastGestureMoved.current)
                                        commitLink(g); } : undefined, children: [onCreateNote && !chains.get(g.id)?.length && !walkFor && (_jsx("button", { type: "button", className: "tgo-walk-btn", title: "Walk it back \u2014 what must be true before this outcome?", onClick: () => openWalkInput(g.id), children: _jsx(ArrowLeft, { size: 11 }) })), _jsxs("div", { className: "tgo-card-top", children: [onUpdateGoal && (_jsxs("button", { type: "button", className: `tgo-flag ${priority ? `tgo-flag-p${priority}` : ''}`, title: priority === 1
                                                        ? 'P1 — THE ONE: the initiative that blows out the number; it earns 80% of the week'
                                                        : priority ? `Priority ${priority} — click to change` : 'Set priority (P1 = the one initiative that gets 80% of your week)', onClick: () => editGoal(g, { priority: (priority + 1) % 4 }, 'change priority'), children: [_jsx(Flag, { size: 11 }), priority > 0 ? ` P${priority}` : ''] })), onUpdateGoal && (_jsx("button", { type: "button", className: `tgo-mini tgo-mini-top ${paletteFor === g.id ? 'tgo-mini-armed' : ''}`, title: "Colour this goal", "aria-label": "Colour", onClick: () => setPaletteFor(paletteFor === g.id ? null : g.id), children: _jsx(Palette, { size: 11 }) })), onUpdateGoal && goals.length > 1 && (_jsx("button", { type: "button", className: `tgo-mini tgo-mini-top ${linkFrom?.id === g.id ? 'tgo-mini-armed' : ''}`, title: linkFrom?.id === g.id ? 'Now click the goal this one feeds (Esc cancels)' : g.parent_goal_id ? 'Connected — click to re-point, or unlink below' : 'Connect — click, then click the goal this one feeds', "aria-label": "Connect", onClick: () => setLinkFrom(linkFrom?.id === g.id ? null : { kind: 'goal', id: g.id }), children: _jsx(Link2, { size: 11 }) })), priority === 1 && p1Share != null && (_jsxs("span", { className: `tgo-one-share ${p1Share >= 0.8 ? 'tgo-one-share-ok' : ''}`, title: `THE ONE took ${Math.round(p1Share * 100)}% of the window's goal-linked work — the doctrine says 80%. Measured from completed task minutes, not a feeling.`, children: [Math.round(p1Share * 100), "%"] })), g.account_label && _jsx("span", { className: "tgo-acct", children: g.account_label }), (g.deal_count || 0) > 0 && (_jsxs("span", { className: "tgo-money", title: `${g.deal_count} linked deal${(g.deal_count || 0) === 1 ? '' : 's'} in the pot — this goal feeds the number`, children: ["$", ((g.deal_weighted || 0) / 1e6).toFixed((g.deal_weighted || 0) >= 1e7 ? 0 : 1), "M"] })), maturity === 'smart' && _jsx("span", { className: "tgo-smart-badge", title: "All five letters lit \u2014 this is a SMART goal", children: "SMART" }), g.starved && _jsx("span", { className: "tgo-warn", title: "Actionable steps exist but nothing has completed in 14 days \u2014 this goal is being starved", children: "starved" })] }), paletteFor === g.id && onUpdateGoal && renderPalette(g.color, (c) => editGoal(g, { color: c }, 'recolour goal')), isEd('outcome') ? (_jsx("textarea", { className: "tgo-edit tgo-edit-outcome", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => commitFacet(g), onKeyDown: facetKeys(g) })) : (_jsx("button", { type: "button", className: "tgo-outcome", onClick: () => onUpdateGoal && openFacet(g, 'outcome'), title: "Click to rewrite", children: g.outcome })), _jsxs("div", { className: "tgo-letters", role: "group", "aria-label": "SMART facets", children: [['S', 'M', 'A', 'R', 'T'].map((l) => (_jsx("button", { type: "button", className: `tgo-letter ${letters[l] ? 'tgo-letter-lit' : ''} ${l === 'A' && redecomposeArmed === g.id ? 'tgo-letter-armed' : ''}`, title: l === 'A' && letters.A
                                                        ? (redecomposeArmed === g.id
                                                            ? 'Click again to re-plan: done steps stay, open steps are replaced'
                                                            : 'A — answered. Click twice to RE-PLAN the open steps (done ones stay)')
                                                        : letters[l] ? `${l} — answered` : LETTER_QUESTION[l], onClick: () => {
                                                        if (l === 'A') {
                                                            if (!letters.A)
                                                                decompose(g);
                                                            else
                                                                armRedecompose(g);
                                                            return;
                                                        }
                                                        if (onUpdateGoal)
                                                            openFacet(g, l);
                                                    }, children: l }, l))), (g.measure || letters.T) && !isEd('M') && !isEd('T') && (_jsxs("span", { className: "tgo-facts", children: [g.measure && _jsx("span", { title: "The measure", children: g.measure }), g.horizon && _jsx("span", { title: "Horizon", children: g.horizon.slice(0, 10) })] }))] }), (gPeople.length > 0 || onUpdateGoal) && (_jsxs("div", { className: "tgo-attach", children: [gPeople.map((pp, pi) => (_jsxs("span", { className: "tgo-person", title: pp.email || pp.name, children: [pp.name, onUpdateGoal && (_jsx("button", { type: "button", className: "tgo-person-x", "aria-label": `Remove ${pp.name}`, onClick: () => editGoal(g, { people: gPeople.filter((_, xi) => xi !== pi) }, `remove ${pp.name}`), children: _jsx(X, { size: 9 }) }))] }, `${pp.name}-${pi}`))), onUpdateGoal && (editing?.id === g.id && editing.facet === 'person' ? (_jsxs(_Fragment, { children: [_jsx("input", { className: "tgo-edit tgo-edit-person", list: "tgo-people", placeholder: "Who does this need?", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => commitFacet(g), onKeyDown: facetKeys(g) }), people && people.length > 0 && (_jsx("datalist", { id: "tgo-people", children: people.map((pp) => _jsx("option", { value: pp.name }, `${pp.name}-${pp.email || ''}`)) }))] })) : (_jsx("button", { type: "button", className: "tgo-mini", title: "Attach a person \u2014 the collaborator, the target, the one who knows them", onClick: () => openFacet(g, 'person'), children: _jsx(UserPlus, { size: 10 }) })))] })), editing?.id === g.id && editing.facet !== 'outcome' && editing.facet !== 'person' && editing.facet !== 'note' && (_jsx("div", { className: "tgo-facet", children: editing.facet === 'T' ? (_jsx("input", { className: "tgo-edit", type: "date", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => commitFacet(g), onKeyDown: facetKeys(g) })) : editing.facet === 'S' ? (_jsxs(_Fragment, { children: [_jsx("input", { className: "tgo-edit", list: "tgo-accounts", placeholder: "Account this lands on", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => commitFacet(g), onKeyDown: facetKeys(g) }), accounts && accounts.length > 0 && (_jsx("datalist", { id: "tgo-accounts", children: accounts.map((a) => _jsx("option", { value: a }, a)) }))] })) : (_jsx("input", { className: "tgo-edit", placeholder: editing.facet === 'M' ? 'e.g. Signed XSIAM order ≥ $400k by the horizon' : 'Why this matters', value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onBlur: () => commitFacet(g), onKeyDown: facetKeys(g) })) })), firstUnlit && !editing && (_jsx("button", { type: "button", className: "tgo-coach", disabled: busy, onClick: () => {
                                                if (firstUnlit === 'A')
                                                    decompose(g);
                                                else if (onUpdateGoal)
                                                    openFacet(g, firstUnlit);
                                            }, children: busy ? (_jsxs("span", { className: "tgo-coach-act", children: [_jsx(Loader2, { size: 11, className: "tgl-spin" }), " processing the brainstorm\u2026"] })) : (_jsxs(_Fragment, { children: [LETTER_QUESTION[firstUnlit], firstUnlit === 'A' && onDecomposeGoal && (_jsxs("span", { className: "tgo-coach-act", children: [_jsx(Sparkles, { size: 11 }), " decompose now"] }))] })) })), planExists && g.plan_stale && !editing && (_jsx("button", { type: "button", className: "tgo-coach tgo-coach-stale", disabled: busy, title: "Nothing on this plan has finished in 21 days \u2014 the open steps may no longer be the right steps. Re-planning keeps done steps and replaces open ones (they archive, recoverable).", onClick: () => armRedecompose(g), children: busy ? (_jsxs("span", { className: "tgo-coach-act", children: [_jsx(Loader2, { size: 11, className: "tgl-spin" }), " re-drawing the plan\u2026"] })) : redecomposeArmed === g.id ? ('Sure? Open steps are replaced — done ones stay. Click again.') : (_jsxs(_Fragment, { children: ["The plan has gone stale \u2014 nothing finished in 21 days.", _jsxs("span", { className: "tgo-coach-act", children: [_jsx(Sparkles, { size: 11 }), " re-plan the open steps"] })] })) })), priority === 1 && (g.sync_count || 0) === 0 && !editing && (onBookRhythm ? (_jsxs("button", { type: "button", className: "tgo-coach", title: "Vision \u00A76: critical goals need critical standing meetings \u2014 this opens the sync form already linked to this goal", onClick: () => onBookRhythm(g.id), children: ["THE ONE has no standing rhythm \u2014 book the recurring meeting with its key players.", _jsxs("span", { className: "tgo-coach-act", children: [_jsx(CalendarPlus, { size: 11 }), " book it"] })] })) : (_jsx("div", { className: "tgo-coach", children: "THE ONE has no standing rhythm \u2014 book the recurring meeting with its key players." }))), planExists && (() => {
                                            const steps = goalTasks
                                                .filter((t) => !t.is_project && t.status !== 'completed' && !t.archived_at)
                                                .slice(0, 4);
                                            if (!steps.length)
                                                return null;
                                            return (_jsxs("ol", { className: "tgo-plan", children: [steps.map((t) => (_jsx("li", { className: "tgo-plan-step", title: t.title, children: t.title }, t.id))), open > steps.length && (_jsxs("li", { className: "tgo-plan-more", children: ["+", open - steps.length, " more open"] }))] }));
                                        })(), anchors('goal', g.id), onUpdateGoal && (_jsx("span", { className: "tgo-resize tgo-resize-e", role: "presentation", title: "Drag to set the card width \u00B7 double-click for the default", onPointerDown: (e) => startResize(e, 'goal', g.id, 'x'), onPointerMove: movePointer, onPointerUp: endPointer, onPointerCancel: cancelPointer, onDoubleClick: (e) => { e.stopPropagation(); resetSize('goal', g.id); } })), _jsxs("div", { className: "tgo-card-foot", children: [(open > 0 || done > 0) && (_jsxs("span", { className: "tgo-steps", title: `${done} done · ${open} open steps`, children: [done, "/", done + open, " steps"] })), onUpdateStatus && (_jsxs("span", { className: "tgo-status-acts", children: [g.parent_goal_id && onUpdateGoal && (_jsx("button", { type: "button", className: "tgo-mini", title: parentOutcome ? `Feeds “${parentOutcome}” — click to disconnect` : 'Feeds a goal that is not on the board (paused or achieved — see Rhythms & registries) — click to disconnect', onClick: () => editGoal(g, { parent_goal_id: null }, 'disconnect'), children: _jsx(Unlink, { size: 11 }) })), _jsx("button", { type: "button", className: "tgo-mini", title: "Pause \u2014 it moves to Rhythms & registries; \u2318Z brings it back", onClick: () => changeStatus(g, 'paused'), children: _jsx(Pause, { size: 11 }) }), _jsx("button", { type: "button", className: "tgo-mini tgo-mini-win", title: "Achieved", onClick: () => changeStatus(g, 'achieved'), children: _jsx(Check, { size: 11 }) }), abandonArmed === g.id ? (_jsx("button", { type: "button", className: "tgo-mini tgo-mini-danger", title: "Confirm abandon", onClick: () => { setAbandonArmed(null); changeStatus(g, 'abandoned'); }, children: "sure?" })) : (_jsx("button", { type: "button", className: "tgo-mini", title: "Abandon", onClick: () => setAbandonArmed(g.id), children: _jsx(Trash2, { size: 11 }) }))] }))] })] }, g.id));
                            })] }), pipeline && (() => {
                        const m = pipeline.math;
                        const q = pipeline.quota?.amount || 0;
                        const fmtM = (n) => (n >= 950000
                            ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`
                            : `$${Math.round(n / 1e3)}k`);
                        const pct = (n) => `${q > 0 ? Math.min(100, (n / q) * 100) : 0}%`;
                        const openDeals = (pipeline.deals || []).filter((dl) => dl.status === 'open');
                        const chip = (dl) => {
                            const heat = dl.probability >= 70 ? 'hot' : dl.probability >= 40 ? 'warm' : 'cold';
                            return (_jsxs("button", { type: "button", className: `tgo-deal tgo-deal-${heat}`, title: `${dl.name || dl.account_label || 'deal'} — ${fmtM(dl.amount)} at ${dl.probability}% — drag between pot and bench, or onto a goal card to link it; tap to open The Number`, onPointerDown: (ev) => { ev.stopPropagation(); startDealDrag(ev, dl); }, onPointerMove: moveDealDrag, onPointerUp: endDealDrag, children: [_jsx("span", { className: "tgo-deal-name", children: dl.name || dl.account_label || 'deal' }), _jsx("span", { className: "tgo-deal-amt", children: fmtM(dl.amount) }), dl.goal_id && _jsx("span", { className: "tgo-deal-linked", title: "Drives a goal on this board", children: "\u25C6" })] }, dl.id));
                        };
                        return (_jsxs("div", { className: "tgo-dock-wrap", children: [_jsxs("div", { className: "tgo-dock", children: [_jsxs("button", { type: "button", className: "tgo-dock-main", onClick: onOpenNumber, title: q > 0 && m
                                                ? `${pipeline.fy}: ${fmtM(m.won)} won · ${fmtM(m.weighted)} weighted · ${m.gap != null ? `${fmtM(m.gap)} gap` : 'no gap math'} — open The Number`
                                                : `No quota set for ${pipeline.fy} — open The Number and set it`, children: [_jsx("span", { className: "tgo-dock-fy", children: pipeline.fy }), q > 0 && m ? (_jsxs(_Fragment, { children: [_jsxs("span", { className: "tgo-dock-bar", "aria-hidden": "true", children: [_jsx("span", { className: "tgo-dock-weighted", style: { width: pct(m.won + m.weighted) } }), _jsx("span", { className: "tgo-dock-won", style: { width: pct(m.won) } })] }), m.gap != null && m.gap > 0 ? (_jsxs("span", { className: "tgo-dock-gap", children: [fmtM(m.gap), " gap"] })) : (_jsx("span", { className: "tgo-dock-covered", children: "covered" }))] })) : (_jsx("span", { className: "tgo-dock-gap", children: "set the number" }))] }), onUpdateDeal && (_jsxs("button", { type: "button", className: `tgo-dock-toggle ${dockOpen ? 'tgo-dock-toggle-open' : ''}`, title: dockOpen ? 'Hide the deal tray' : `Deal tray — ${openDeals.length} open deal${openDeals.length === 1 ? '' : 's'}`, "aria-expanded": dockOpen, onClick: () => setDockOpen((o) => !o), children: [_jsx(ChevronDown, { size: 12 }), openDeals.length > 0 && _jsx("span", { className: "tgo-dock-count", children: openDeals.length })] }))] }), dockOpen && onUpdateDeal && (_jsxs("div", { className: "tgo-dock-tray", children: [_jsxs("div", { ref: potRef, className: `tgo-dock-row ${dockZone === 'pot' ? 'tgo-dock-row-hot' : ''}`, children: [_jsx("span", { className: "tgo-dock-row-label", title: "Committed to the number \u2014 counts in the weighted math", children: "pot" }), openDeals.filter((dl) => dl.in_quota).map(chip), openDeals.filter((dl) => dl.in_quota).length === 0 && (_jsx("span", { className: "tgo-dock-empty", children: "drag a deal in" }))] }), _jsxs("div", { ref: benchRef, className: `tgo-dock-row ${dockZone === 'bench' ? 'tgo-dock-row-hot' : ''}`, children: [_jsx("span", { className: "tgo-dock-row-label", title: "Visible, not counted", children: "bench" }), openDeals.filter((dl) => !dl.in_quota).map(chip), openDeals.filter((dl) => !dl.in_quota).length === 0 && (_jsx("span", { className: "tgo-dock-empty", children: "empty" }))] }), _jsx("div", { className: "tgo-dock-hint", children: "drop a chip on a goal card to link money to plan" })] }))] }));
                    })(), dealGhost && (_jsxs("div", { className: "tgo-deal tgo-deal-ghost", style: { left: dealGhost.x + 10, top: dealGhost.y + 8 }, children: [_jsx("span", { className: "tgo-deal-name", children: dealGhost.deal.name || dealGhost.deal.account_label || 'deal' }), _jsx("span", { className: "tgo-deal-amt", children: dealGhost.deal.amount >= 950000
                                    ? `$${(dealGhost.deal.amount / 1e6).toFixed(1)}M`
                                    : `$${Math.round(dealGhost.deal.amount / 1e3)}k` })] })), sel.size > 0 && !dragging && (() => {
                        const b = boundsOf(selectedObjects);
                        if (!b)
                            return null;
                        const top = pz.worldToScreen((b.minX + b.maxX) / 2, b.minY);
                        const noteCount = selectedNotes.length;
                        return (_jsxs("div", { className: "tgo-selbar", style: { left: Math.round(top.x), top: Math.round(top.y) }, onPointerDown: (e) => e.stopPropagation(), children: [_jsxs("span", { className: "tgo-selbar-count", children: [sel.size, " selected"] }), (onUpdateGoal || onUpdateNote) && renderPalette(null, colorSelection), (onUpdateGoal || onUpdateNote) && (_jsxs("span", { className: "tgo-selbar-scale", children: [_jsx("button", { type: "button", className: "tgo-mini tgo-mini-a", title: "Smaller text", onClick: () => scaleSelection(-1), children: "A" }), _jsx("button", { type: "button", className: "tgo-mini tgo-mini-A", title: "Bigger text \u2014 make this one the headline", onClick: () => scaleSelection(1), children: "A" })] })), onCreateObject && (_jsxs("button", { type: "button", className: "tgo-mini tgo-mini-wide", title: "Draw a region around these \u2014 name it, drag it as one, and later hand the whole thing to the assistant", onClick: makeRegion, children: [_jsx(Frame, { size: 11 }), " Region"] })), onCreateNote && noteCount > 0 && (_jsx("button", { type: "button", className: "tgo-mini", title: `Duplicate ${noteCount} thought${noteCount === 1 ? '' : 's'} (⌘D)`, onClick: duplicateSelection, children: _jsx(Copy, { size: 11 }) })), onDeleteNote && noteCount > 0 && (_jsx("button", { type: "button", className: "tgo-mini tgo-mini-danger", title: `Delete ${noteCount} thought${noteCount === 1 ? '' : 's'} (Del) — ⌘Z brings them back${noteCount < sel.size ? '. Goals are paused or abandoned, never deleted' : ''}`, onClick: deleteSelection, children: _jsx(Trash2, { size: 11 }) })), _jsx("button", { type: "button", className: "tgo-mini", title: "Clear the selection (Esc)", onClick: () => sel.clear(), children: _jsx(X, { size: 11 }) })] }));
                    })(), proposal && (_jsxs("aside", { className: "tgo-prop", role: "dialog", "aria-label": "Proposed plan", children: [_jsxs("div", { className: "tgo-prop-head", children: [_jsx("span", { className: "tgo-prop-title", children: proposal.data.title }), proposal.data.source === 'floor' && (_jsx("span", { className: "tgo-prop-floor", title: "The assistant is off or its daily budget is spent, so this is the plain reading: every thought as a candidate, verbatim.", children: "no assistant" })), _jsx("button", { type: "button", className: "tgo-mini", title: "Close without creating anything", onClick: () => setProposal(null), children: _jsx(X, { size: 11 }) })] }), _jsxs("div", { className: "tgo-prop-body", children: [proposal.data.themes.length > 0 && (_jsxs("div", { className: "tgo-prop-block", children: [_jsx("div", { className: "tgo-prop-label", children: "Themes" }), proposal.data.themes.map((t) => (_jsxs("div", { className: "tgo-prop-theme", children: [t.label, " ", _jsx("span", { className: "tgo-prop-n", children: t.source_ids.length })] }, t.label)))] })), !!proposal.data.unverified && (_jsxs("p", { className: "tgo-prop-empty", children: [proposal.data.unverified, " of these thoughts", proposal.data.unverified === 1 ? ' is a reading' : ' are readings', " off a photo that nobody has checked.", !!onUpdateNote && (_jsxs(_Fragment, { children: [' ', _jsx("button", { type: "button", className: "tgo-prop-src", onClick: () => {
                                                            const ids = new Set(proposal.data.unverified_ids || []);
                                                            const first = unsure.find((n) => ids.has(n.id)) || unsure[0];
                                                            setProposal(null);
                                                            checkTo(first);
                                                        }, children: "check them first" })] }))] })), _jsxs("div", { className: "tgo-prop-block", children: [_jsxs("div", { className: "tgo-prop-label", children: ["Action items", _jsxs("span", { className: "tgo-prop-n", children: [accepted.size, "/", proposal.data.actions.length] })] }), proposal.data.actions.length === 0 && (_jsx("p", { className: "tgo-prop-empty", children: "Nothing here reads as a next step yet." })), proposal.data.actions.map((a, i) => (_jsxs("label", { className: `tgo-prop-act ${accepted.has(i) ? 'tgo-prop-on' : ''}`, children: [_jsx("input", { type: "checkbox", checked: accepted.has(i), onChange: () => setAccepted((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(i))
                                                                next.delete(i);
                                                            else
                                                                next.add(i);
                                                            return next;
                                                        }) }), _jsxs("span", { className: "tgo-prop-act-body", children: [_jsx("span", { className: "tgo-prop-act-title", children: a.title }), a.why && _jsx("span", { className: "tgo-prop-why", children: a.why }), a.rests_on_unverified && (_jsx("span", { className: "tgo-prop-why", children: "rests on a reading nobody has checked yet" })), _jsxs("span", { className: "tgo-prop-meta", children: [(people || []).length > 0 ? (_jsxs("select", { className: "tgo-prop-who", value: ownerValue(a), title: "Who is this waiting on?", onClick: (e) => e.preventDefault(), onChange: (e) => setOwner(i, e.target.value), children: [_jsx("option", { value: "", children: "no owner \u2014 mine" }), a.owner_hint && !(people || []).some((pp) => pp.name === a.owner_hint) && (_jsxs("option", { value: a.owner_hint, children: [a.owner_hint, " (not in your contacts)"] })), (people || []).map((pp) => (_jsxs("option", { value: pp.contact_id || pp.name, children: [pp.name, pp.hint ? ` · ${pp.hint}` : ''] }, pp.contact_id || pp.email || pp.name)))] })) : (a.owner_hint && _jsxs("span", { className: "tgo-prop-owner", children: ["waiting on ", a.owner_hint] })), a.due_hint && _jsx("span", { className: "tgo-prop-due", children: a.due_hint }), !!a.source_ids?.length && (_jsxs("button", { type: "button", className: "tgo-prop-src", title: "Select the thoughts this came from", onClick: (e) => { e.preventDefault(); sel.setSelection(a.source_ids || []); }, children: ["from ", a.source_ids.length, " thought", a.source_ids.length === 1 ? '' : 's'] }))] })] })] }, `${a.title}-${i}`)))] }), proposal.data.gaps.length > 0 && (_jsxs("div", { className: "tgo-prop-block", children: [_jsx("div", { className: "tgo-prop-label", children: "This does not answer yet" }), proposal.data.gaps.map((g) => _jsx("div", { className: "tgo-prop-gap", children: g }, g))] })), !!proposal.data.already?.length && (_jsxs("div", { className: "tgo-prop-block", children: [_jsx("div", { className: "tgo-prop-label", children: "Already captured from this region" }), proposal.data.already.map((t) => _jsx("div", { className: "tgo-prop-done", children: t.title }, t.id))] }))] }), _jsxs("div", { className: "tgo-prop-foot", children: [onCommitRegion && (_jsxs("button", { type: "button", className: "tgo-prop-go", disabled: !accepted.size || committing, onClick: commit, children: [committing ? _jsx(Loader2, { size: 12, className: "tgl-spin" }) : _jsx(Check, { size: 12 }), "Create ", accepted.size, " task", accepted.size === 1 ? '' : 's'] })), _jsx("span", { className: "tgo-prop-note", children: "Nothing is created until you press this." })] })] })), shot && (_jsxs("aside", { className: "tgo-prop tgo-prop-shot", role: "dialog", "aria-label": "Stickies read from the photo", children: [_jsxs("div", { className: "tgo-prop-head", children: [_jsx("span", { className: "tgo-prop-title", children: shot.data.source === 'ai'
                                            ? `${shot.data.stickies.length} ${shot.data.stickies.length === 1 ? 'sticky' : 'stickies'} read`
                                            : 'Nothing was read' }), shot.data.structure && !shot.data.structure.read && (_jsx("span", { className: "tgo-prop-floor", title: "The words came back but the whole-board look did not, so any boxes or lines drawn on this whiteboard were not read. The stickies below are unaffected.", children: "no structure read" })), _jsx("button", { type: "button", className: "tgo-mini", title: "Close", onClick: () => setShot(null), children: _jsx(X, { size: 12 }) })] }), _jsxs("div", { className: "tgo-prop-body", children: [shot.data.source !== 'ai' && (_jsx("p", { className: "tgo-prop-empty", children: shot.data.reason })), shot.data.source === 'ai' && shot.data.stickies.length === 0 && (_jsx("p", { className: "tgo-prop-empty", children: "No handwriting was found in this photo. If the board is there but faint, try a straighter shot with more light." })), shot.data.reimport && (_jsxs("p", { className: "tgo-prop-empty", children: [shot.data.reimport.on_board, " note", shot.data.reimport.on_board === 1 ? ' is' : 's are', " already under this photo, so this is a comparison, not a second import.", !!shot.data.reimport.unmatched_existing && (_jsxs(_Fragment, { children: [' ', shot.data.reimport.unmatched_existing, " of them", shot.data.reimport.unmatched_existing === 1 ? ' was' : ' were', " not found in the photo \u2014 left alone, because a photo that missed one looks the same as a note that came down."] }))] })), shot.data.stickies.map((st, i) => {
                                        const status = st.status ?? 'new';
                                        return (_jsxs("label", { className: `tgo-prop-act ${takenIn.has(i) ? 'tgo-prop-on' : ''} ${status === 'same' ? 'tgo-prop-quiet' : ''}`, children: [_jsx("input", { type: "checkbox", checked: takenIn.has(i), onChange: () => setTakenIn((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(i))
                                                            next.delete(i);
                                                        else
                                                            next.add(i);
                                                        return next;
                                                    }) }), _jsx("span", { className: `tgo-prop-swatch ${st.color ? toneClass(st.color) : ''}`, "aria-hidden": "true" }), _jsxs("span", { className: "tgo-prop-act-body", children: [_jsx("span", { className: "tgo-prop-act-title", children: st.text }), status === 'same' && (_jsx("span", { className: "tgo-prop-why", children: "already on the board, unchanged" })), status === 'moved' && (_jsx("span", { className: "tgo-prop-why", children: "already on the board \u2014 this moves it here" })), status === 'edited' && (_jsxs("span", { className: "tgo-prop-why", children: ["replaces \u201C", st.match_text, "\u201D on the board"] })), status !== 'same' && st.legibility < 0.6 && (_jsx("span", { className: "tgo-prop-why", children: "hard to read \u2014 check this against the photo before you take it" }))] })] }, `${st.text}-${i}`));
                                    }), !!shot.data.regions?.length && (_jsxs("div", { className: "tgo-prop-block", children: [_jsxs("div", { className: "tgo-prop-label", children: ["Groups drawn on the board", _jsxs("span", { className: "tgo-prop-n", children: [takenRegions.size, "/", shot.data.regions.length] })] }), shot.data.regions.map((r, i) => (_jsxs("label", { className: `tgo-prop-act ${takenRegions.has(i) ? 'tgo-prop-on' : ''} ${r.status === 'same' ? 'tgo-prop-quiet' : ''}`, children: [_jsx("input", { type: "checkbox", checked: takenRegions.has(i), onChange: () => setTakenRegions((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(i))
                                                                next.delete(i);
                                                            else
                                                                next.add(i);
                                                            return next;
                                                        }) }), _jsx(Frame, { size: 11, className: "tgo-prop-ico", "aria-hidden": "true" }), _jsxs("span", { className: "tgo-prop-act-body", children: [_jsx("span", { className: "tgo-prop-act-title", children: r.label || 'Unnamed group' }), _jsx("span", { className: "tgo-prop-why", children: r.status === 'same'
                                                                    ? 'already a region on the board'
                                                                    : 'a region you can then polish and process' })] })] }, `r${i}`)))] })), !!shot.data.connectors?.length && (_jsxs("div", { className: "tgo-prop-block", children: [_jsxs("div", { className: "tgo-prop-label", children: ["Lines between notes", _jsxs("span", { className: "tgo-prop-n", children: [liveLinks, "/", shot.data.connectors.length] })] }), shot.data.connectors.map((c, i) => {
                                                const live = linkIsLive(c);
                                                const a = shot.data.stickies[c.from_index]?.text || '?';
                                                const b = shot.data.stickies[c.to_index]?.text || '?';
                                                return (_jsxs("label", { className: `tgo-prop-act ${takenLinks.has(i) && live ? 'tgo-prop-on' : ''} ${live ? '' : 'tgo-prop-dead'}`, children: [_jsx("input", { type: "checkbox", checked: takenLinks.has(i) && live, disabled: !live, onChange: () => setTakenLinks((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(i))
                                                                    next.delete(i);
                                                                else
                                                                    next.add(i);
                                                                return next;
                                                            }) }), _jsx(Link2, { size: 11, className: "tgo-prop-ico", "aria-hidden": "true" }), _jsxs("span", { className: "tgo-prop-act-body", children: [_jsxs("span", { className: "tgo-prop-act-title tgo-prop-join", children: [_jsx("span", { className: "tgo-prop-end", children: a }), _jsx("span", { className: "tgo-prop-rel", "aria-label": c.kind === 'arrow' ? 'points to' : 'joined to', children: c.kind === 'arrow' ? '→' : '—' }), _jsx("span", { className: "tgo-prop-end", children: b })] }), c.label && _jsxs("span", { className: "tgo-prop-why", children: ["labelled \u201C", c.label, "\u201D"] }), c.kind === 'line' && c.directed === false && c.direction_confidence > 0 && (_jsx("span", { className: "tgo-prop-why", children: "drawn as a plain line \u2014 which way it points was not clear enough to call" })), !live && (_jsx("span", { className: "tgo-prop-why", children: "needs both notes \u2014 one of them is unticked above and is not on the board" }))] })] }, `c${i}`));
                                            })] })), !!shot.data.structure?.unresolved && (_jsxs("p", { className: "tgo-prop-empty", children: [shot.data.structure.unresolved, " drawn", ' ', shot.data.structure.unresolved === 1 ? 'line joins' : 'lines join', " something that is not a sticky, so ", shot.data.structure.unresolved === 1 ? 'it was' : 'they were', " left on the photo."] }))] }), _jsxs("div", { className: "tgo-prop-foot", children: [onCommitStickies && (shot.data.stickies.length > 0 || !!shot.data.regions?.length) && (_jsxs("button", { type: "button", className: "tgo-prop-go", disabled: (!takenIn.size && !takenRegions.size && !liveLinks) || takingIn, onClick: takeStickies, children: [takingIn ? _jsx(Loader2, { size: 12, className: "tgl-spin" }) : _jsx(Check, { size: 12 }), "Add ", takenIn.size, " ", takenIn.size === 1 ? 'sticky' : 'stickies', !!takenRegions.size && ` + ${takenRegions.size} ${takenRegions.size === 1 ? 'group' : 'groups'}`, !!liveLinks && ` + ${liveLinks} ${liveLinks === 1 ? 'line' : 'lines'}`] })), _jsx("span", { className: "tgo-prop-note", children: "They land on top of the photo, where they were read." })] })] })), !!unsure.length && !checkAt && !shot && (_jsxs("button", { type: "button", className: "tgo-check-cta", title: "Step through the stickies the photo read but nobody has checked", onClick: () => checkTo(unsure[0]), children: [_jsx(HelpCircle, { size: 12 }), "Check ", unsure.length, " read", unsure.length === 1 ? '' : 's'] })), checkNote && (_jsxs("aside", { className: "tgo-prop tgo-prop-shot", role: "dialog", "aria-label": "Check what the photo read", children: [_jsxs("div", { className: "tgo-prop-head", children: [_jsxs("span", { className: "tgo-prop-title", children: ["Checking ", unsure.findIndex((n) => n.id === checkNote.id) + 1, " of ", unsure.length] }), typeof checkNote.source_ref?.legibility === 'number' && (_jsxs("span", { className: "tgo-prop-floor", title: "How sure the reader was when it transcribed this. It is a record of the import and never changes, whatever you decide here.", children: [Math.round(checkNote.source_ref.legibility * 100), "% sure"] })), _jsx("button", { type: "button", className: "tgo-mini", title: "Stop checking", onClick: () => setCheckAt(null), children: _jsx(X, { size: 12 }) })] }), _jsxs("div", { className: "tgo-prop-body", children: [_jsxs("div", { className: "tgo-prop-block", children: [_jsx("div", { className: "tgo-prop-label", children: "What the photo says" }), checkShot?.signed_url && checkNote.source_ref?.px_box ? (_jsx("div", { className: "tgo-crop", children: _jsx("img", { src: checkShot.signed_url, alt: "The handwriting this was read from", draggable: false, style: cropStyle(checkNote.source_ref.px_box) }) })) : (_jsx("p", { className: "tgo-prop-empty", children: "The photo this came from is no longer on the board, so compare it by eye." }))] }), _jsxs("div", { className: "tgo-prop-block", children: [_jsx("div", { className: "tgo-prop-label", children: "What it was read as" }), _jsx("textarea", { className: "tgo-check-text", value: checkDraft, onChange: (e) => setCheckDraft(e.target.value), onKeyDown: (e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        checkConfirm(checkNote, checkDraft);
                                                    }
                                                    if (e.key === 'Escape')
                                                        setCheckAt(null);
                                                }, "aria-label": "The transcription, editable" })] })] }), _jsxs("div", { className: "tgo-prop-foot", children: [_jsx("button", { type: "button", className: "tgo-mini", title: "Leave this one flagged and come back to it", onClick: () => checkStep(1), children: "Skip" }), _jsx("span", { className: "tgo-prop-spacer" }), _jsxs("button", { type: "button", className: "tgo-prop-go", disabled: !checkDraft.trim() || !onUpdateNote, onClick: () => checkConfirm(checkNote, checkDraft), children: [_jsx(Check, { size: 12 }), checkDraft.trim() === checkNote.text ? 'Reads right' : 'Fix and mark checked'] })] })] })), committed && (_jsxs("div", { className: "tgo-prop-toast", role: "status", children: [committed.n, " ", committed.n === 1
                                ? (committed.noun || 'task')
                                : `${committed.noun === 'sticky' ? 'stickies' : `${committed.noun || 'task'}s`}`, " created", committed.skipped ? ` · ${committed.skipped} already there` : '', _jsx("button", { type: "button", className: "tgo-mini", title: "Dismiss", onClick: () => setCommitted(null), children: _jsx(X, { size: 10 }) })] })), _jsxs("div", { className: "tgo-hud", role: "group", "aria-label": "Canvas controls", children: [_jsx("button", { type: "button", className: `tgo-hud-btn ${tool === 'select' ? 'tgo-hud-on' : ''}`, title: "Select \u2014 drag the board to rubber-band, shift to add (V)", "aria-pressed": tool === 'select', onClick: () => setTool('select'), children: _jsx(MousePointer2, { size: 12 }) }), _jsx("button", { type: "button", className: `tgo-hud-btn ${tool === 'lasso' ? 'tgo-hud-on' : ''}`, title: "Lasso \u2014 draw a loop around a cluster (L)", "aria-pressed": tool === 'lasso', onClick: () => setTool('lasso'), children: _jsx(Lasso, { size: 12 }) }), onCreateObject && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: `tgo-hud-btn ${tool === 'pen' ? 'tgo-hud-on' : ''}`, title: "Pen \u2014 draw on the board (P). Ink lands behind the thinking.", "aria-pressed": tool === 'pen', onClick: () => setTool(tool === 'pen' ? 'select' : 'pen'), children: _jsx(PenLine, { size: 12 }) }), _jsx("button", { type: "button", className: `tgo-hud-btn ${tool === 'shape' ? 'tgo-hud-on' : ''}`, title: `Shape — drag out a ${SHAPES.find((x) => x.kind === shapeKind)?.label.toLowerCase()} (S). Click again to change shape.`, "aria-pressed": tool === 'shape', onClick: () => {
                                            // Already holding the shape tool? Then this press means
                                            // "a different shape", which is what you want next far
                                            // more often than putting the tool down.
                                            if (tool === 'shape') {
                                                const i = SHAPES.findIndex((x) => x.kind === shapeKind);
                                                setShapeKind(SHAPES[(i + 1) % SHAPES.length].kind);
                                            }
                                            else {
                                                setTool('shape');
                                            }
                                        }, children: shapeKind === 'ellipse' ? _jsx(Circle, { size: 12 })
                                            : shapeKind === 'diamond' ? _jsx(Diamond, { size: 12 })
                                                : shapeKind === 'arrow' ? _jsx(MoveRight, { size: 12 })
                                                    : _jsx(Square, { size: 12 }) })] })), _jsx("span", { className: "tgo-hud-sep", "aria-hidden": "true" }), _jsxs("button", { type: "button", className: "tgo-hud-btn tgo-hud-undo", disabled: !history.canUndo || history.busy, title: history.canUndo ? `Undo ${history.undoLabel} (⌘Z)` : 'Nothing to undo', onClick: undo, children: [history.busy ? _jsx(Loader2, { size: 12, className: "tgl-spin" }) : _jsx(Undo2, { size: 12 }), history.count > 0 && _jsx("span", { className: "tgo-hud-count", children: history.count })] }), _jsx("button", { type: "button", className: "tgo-hud-btn", disabled: !history.canRedo || history.busy, title: history.canRedo ? `Redo ${history.redoLabel} (⌘⇧Z)` : 'Nothing to redo', onClick: redo, children: _jsx(Redo2, { size: 12 }) }), _jsx("span", { className: "tgo-hud-sep", "aria-hidden": "true" }), _jsx("button", { type: "button", className: "tgo-hud-btn", title: "Zoom out", onClick: () => pz.zoomBy(1 / 1.25), children: _jsx(Minus, { size: 12 }) }), _jsxs("button", { type: "button", className: "tgo-hud-pct", title: "Reset zoom", onClick: () => pz.reset(), children: [Math.round(view.z * 100), "%"] }), _jsx("button", { type: "button", className: "tgo-hud-btn", title: "Zoom in", onClick: () => pz.zoomBy(1.25), children: _jsx(Plus, { size: 12 }) }), _jsx("button", { type: "button", className: "tgo-hud-btn", title: "Fit everything", onClick: fitAll, children: _jsx(Maximize2, { size: 12 }) }), _jsx("span", { className: "tgo-hud-sep", "aria-hidden": "true" }), _jsx("button", { type: "button", className: "tgo-hud-btn", title: full ? 'Leave full page (Esc)' : 'Full page', "aria-pressed": full, onClick: () => setFull((f) => !f), children: full ? _jsx(Shrink, { size: 12 }) : _jsx(Expand, { size: 12 }) })] })] })] }));
}
export default GoalCanvas;
